import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Badge,
  Divider,
  IconButton,
  Spinner,
  Stack,
  VStack,
  Text,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  FiBell,
  FiBookOpen,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiDroplet,
  FiCamera,
  FiFile,
  FiMapPin,
  FiPackage,
  FiPercent,
  FiMinus,
  FiPlus,
  FiRefreshCcw,
  FiSearch,
  FiShield,
  FiSmile,
  FiTruck,
  FiUploadCloud,
  FiUserCheck,
  FiTrash2,
  FiZap,
  FiChevronDown,
  FiChevronUp,
  FiFileText,
  FiExternalLink,
  FiCalendar,
} from "react-icons/fi";
import "../UI/home.css";
import WithSubnavigation from "./navbar";
import { performLogout } from "../../utils/logout";
import Sidebar from "./side";
import OnboardingOverlay from "../UI/OnboardingOverlay";
import PharmacyMap from "../UI/PharmacyMap";
import PharmacyOfferCards from "./UploadPrescription/PharmacyOfferCards";
import LoginForm from "../UI/LoginForm";
import {
  colors,
  gradients,
  radii,
  shadows,
} from "../UI/designSystem";
import { AnimStyles, SocialProof } from "../UI/Primitives";
import { setScannedPrescriptionFile, clearScannedPrescriptionFile, setPendingSavedPrescriptions } from "../../prescriptionStore";
import { API_BASE } from "../../config";
import { resolveFileUrl } from "../../utils/fileUrl";

const HERO_FLOW_STEPS = [
  {
    eyebrow: "Upload",
    title: "Capture prescription in one tap",
    subtitle: "Camera or PDF upload. No manual data entry to get started.",
  },
  {
    eyebrow: "AI Extraction",
    title: "Medicines, dosage, and quantity are parsed automatically",
    subtitle: "We turn the prescription into a structured medicine list in seconds.",
  },
  {
    eyebrow: "Compare",
    title: "Nearby pharmacies respond with live stock and pricing",
    subtitle: "Choose the best deal, the fastest ETA, or the strongest stock match.",
  },
  {
    eyebrow: "Delivery",
    title: "Lock the order and get it delivered fast",
    subtitle: "The same flow carries through to order review, payment, and fulfillment.",
  },
];

const HeroScene = ({ step }) => {
  const baseWrap = {
    w: "100%",
    h: "210px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  // Step 0 — Upload prescription
  if (step === 0) {
    return (
      <Box {...baseWrap}>
        <svg width="100%" height="100%" viewBox="0 0 240 180" fill="none">
          <defs>
            <linearGradient id="psUpCard" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#FFFFFF" />
              <stop offset="1" stopColor="#F8FAFC" />
            </linearGradient>
            <filter id="psUpShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="6" stdDeviation="6" floodColor="#0F172A" floodOpacity="0.12" />
            </filter>
          </defs>
          {/* Soft background blob */}
          <ellipse cx="120" cy="150" rx="80" ry="10" fill="rgba(13,158,140,0.10)" />

          {/* Prescription card with gentle float */}
          <g filter="url(#psUpShadow)" style={{ transformOrigin: "120px 90px", animation: "psHeroFloatSoft 4.5s ease-in-out infinite" }}>
            <rect x="66" y="22" width="108" height="124" rx="14" fill="url(#psUpCard)" stroke="#E2E8F0" strokeWidth="1" />
            <path d="M66 34 a12 12 0 0 1 12 -12 h84 a12 12 0 0 1 12 12 v18 h-108 z" fill="#0D9E8C" />
            <text x="78" y="44" fill="white" fontSize="11" fontWeight="900" fontFamily="Inter, sans-serif" letterSpacing="0.08em">PRESCRIPTION</text>
            <rect x="156" y="30" width="14" height="14" rx="4" fill="rgba(255,255,255,0.25)" />
            <path d="M160 37 h6 M163 34 v6" stroke="white" strokeWidth="1.6" strokeLinecap="round" />

            {/* Paper content lines */}
            <rect x="78" y="66" width="60" height="5" rx="2.5" fill="#0D9E8C" opacity="0.6" />
            <rect x="78" y="80" width="80" height="4" rx="2" fill="#E2E8F0" />
            <rect x="78" y="92" width="64" height="4" rx="2" fill="#E2E8F0" />
            <rect x="78" y="104" width="72" height="4" rx="2" fill="#E2E8F0" />
            <rect x="78" y="116" width="48" height="4" rx="2" fill="#E2E8F0" />

            {/* Scanning beam */}
            <rect x="68" y="56" width="104" height="2" fill="url(#psScanBeam)" style={{ animation: "psHeroScan 2.6s ease-in-out infinite" }} />
            <defs>
              <linearGradient id="psScanBeam" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="#0D9E8C" stopOpacity="0" />
                <stop offset="0.5" stopColor="#0D9E8C" stopOpacity="1" />
                <stop offset="1" stopColor="#0D9E8C" stopOpacity="0" />
              </linearGradient>
            </defs>
          </g>

          {/* Upload cloud + arrow */}
          <g style={{ transformOrigin: "200px 50px", animation: "psHeroFloatSoft 3.6s ease-in-out .3s infinite" }}>
            <circle cx="200" cy="50" r="22" fill="#CFF3EE" />
            <circle cx="200" cy="50" r="16" fill="#0D9E8C" />
            <path d="M200 58 v-14 M194 50 l6 -6 l6 6" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </g>

          {/* Subtle sparkles */}
          <circle cx="40" cy="58" r="3" fill="#63D4C4" opacity="0.7" style={{ animation: "psHeroBadgePulse 2.2s ease-in-out infinite" }} />
          <circle cx="52" cy="130" r="2.5" fill="#FF9666" opacity="0.7" style={{ animation: "psHeroBadgePulse 2.6s ease-in-out .5s infinite" }} />
        </svg>
      </Box>
    );
  }

  // Step 1 — AI extraction
  if (step === 1) {
    const orbits = [
      { x: 58, y: 56, label: "Amoxicillin", c: "#0D9E8C", d: "0s" },
      { x: 184, y: 52, label: "Dolo 650", c: "#FF6B35", d: "0.5s" },
      { x: 188, y: 120, label: "Cetirizine", c: "#3B82F6", d: "1s" },
      { x: 54, y: 124, label: "Vitamin D3", c: "#10B981", d: "1.5s" },
    ];
    return (
      <Box {...baseWrap}>
        <svg width="100%" height="100%" viewBox="0 0 240 180" fill="none">
          <defs>
            <radialGradient id="psAiCore" cx="40%" cy="40%">
              <stop offset="0" stopColor="#A78BFA" />
              <stop offset="1" stopColor="#6D28D9" />
            </radialGradient>
            <filter id="psAiShadow" x="-40%" y="-40%" width="180%" height="180%">
              <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#6D28D9" floodOpacity="0.35" />
            </filter>
          </defs>

          {/* Orbital rings */}
          <circle cx="120" cy="90" r="62" fill="none" stroke="rgba(124,58,237,0.12)" strokeWidth="1" strokeDasharray="3 4" />
          <circle cx="120" cy="90" r="46" fill="none" stroke="rgba(124,58,237,0.18)" strokeWidth="1" strokeDasharray="3 4" />

          {/* Connector lines to each orbit */}
          {orbits.map((o, i) => (
            <line
              key={`line-${i}`}
              x1="120"
              y1="90"
              x2={o.x}
              y2={o.y}
              stroke={o.c}
              strokeWidth="1.2"
              strokeDasharray="3 4"
              opacity="0.45"
              style={{ animation: `psHeroDashFlow 2.4s linear ${o.d} infinite` }}
            />
          ))}

          {/* AI core */}
          <g filter="url(#psAiShadow)">
            <circle cx="120" cy="90" r="34" fill="rgba(167,139,250,0.18)" style={{ transformOrigin: "120px 90px", animation: "psHeroPulseHalo 3s ease-in-out infinite" }} />
            <circle cx="120" cy="90" r="24" fill="url(#psAiCore)" style={{ transformOrigin: "120px 90px", animation: "psHeroPulseCore 3s ease-in-out infinite" }} />
            <text x="120" y="95" fill="white" fontSize="14" fontWeight="900" fontFamily="Inter, sans-serif" textAnchor="middle">AI</text>
          </g>

          {/* Orbit pill chips with medicine names */}
          {orbits.map((o, i) => (
            <g key={`orbit-${i}`} style={{ transformOrigin: `${o.x}px ${o.y}px`, animation: `psHeroFloatSoft 3.4s ease-in-out ${o.d} infinite` }}>
              <rect x={o.x - 32} y={o.y - 10} width="64" height="20" rx="10" fill="white" stroke={o.c} strokeOpacity="0.35" strokeWidth="1" />
              <circle cx={o.x - 22} cy={o.y} r="4" fill={o.c} />
              <text x={o.x - 12} y={o.y + 3.5} fill="#0F172A" fontSize="8.5" fontWeight="800" fontFamily="Inter, sans-serif">{o.label}</text>
            </g>
          ))}

          {/* Sparkles */}
          <text x="90" y="38" fontSize="11" fill="#A78BFA" style={{ animation: "psHeroBadgePulse 1.8s ease-in-out infinite" }}>✦</text>
          <text x="150" y="158" fontSize="10" fill="#7C3AED" style={{ animation: "psHeroBadgePulse 2s ease-in-out .6s infinite" }}>✦</text>
        </svg>
      </Box>
    );
  }

  // Step 2 — Compare pharmacies
  if (step === 2) {
    return (
      <Box {...baseWrap}>
        <svg width="100%" height="100%" viewBox="0 0 240 180" fill="none">
          <defs>
            <filter id="psCmpShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#0F172A" floodOpacity="0.12" />
            </filter>
          </defs>

          {/* Map background panel */}
          <rect x="18" y="16" width="204" height="148" rx="14" fill="#F0FDF4" stroke="#E2E8F0" strokeWidth="1" />
          {/* Soft road grid */}
          <g opacity="0.7">
            <path d="M18 70 H222" stroke="#DCFCE7" strokeWidth="8" />
            <path d="M120 16 V164" stroke="#DCFCE7" strokeWidth="8" />
            <path d="M70 16 Q60 70 70 164" stroke="#E2E8F0" strokeWidth="2" fill="none" opacity="0.7" />
            <path d="M170 16 Q180 70 170 164" stroke="#E2E8F0" strokeWidth="2" fill="none" opacity="0.7" />
          </g>
          {/* Dashed centerline */}
          <path d="M18 70 H222" stroke="white" strokeWidth="1.5" strokeDasharray="6 6" opacity="0.9" />

          {/* Pharmacy pin — best match */}
          <g style={{ transformOrigin: "76px 78px", animation: "psHeroFloatSoft 3s ease-in-out infinite" }}>
            <circle cx="76" cy="82" r="14" fill="#0D9E8C" opacity="0.2" style={{ transformOrigin: "76px 82px", animation: "psHeroPulseHalo 2.2s ease-in-out infinite" }} />
            <circle cx="76" cy="82" r="10" fill="#0D9E8C" />
            <path d="M72 82 h8 M76 78 v8" stroke="white" strokeWidth="2" strokeLinecap="round" />
            <rect x="90" y="74" width="52" height="16" rx="8" fill="white" stroke="#99F6E4" strokeWidth="1" />
            <text x="98" y="85" fill="#0D9E8C" fontSize="8" fontWeight="900" fontFamily="Inter, sans-serif">★ Best · 0.8km</text>
          </g>

          {/* Pharmacy pin — secondary */}
          <g style={{ transformOrigin: "170px 110px", animation: "psHeroFloatSoft 3.4s ease-in-out .5s infinite" }}>
            <circle cx="170" cy="114" r="10" fill="#94A3B8" opacity="0.18" />
            <circle cx="170" cy="114" r="7" fill="#94A3B8" />
            <path d="M167 114 h6 M170 111 v6" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
            <rect x="112" y="108" width="52" height="14" rx="7" fill="white" stroke="#E2E8F0" strokeWidth="1" />
            <text x="119" y="118" fill="#64748B" fontSize="8" fontWeight="800" fontFamily="Inter, sans-serif">1.2 km · ₹238</text>
          </g>

          {/* Pharmacy pin — tertiary */}
          <g style={{ transformOrigin: "130px 42px", animation: "psHeroFloatSoft 3.8s ease-in-out 1s infinite" }}>
            <circle cx="130" cy="44" r="8" fill="#94A3B8" opacity="0.16" />
            <circle cx="130" cy="44" r="5.5" fill="#94A3B8" />
            <path d="M128 44 h4 M130 42 v4" stroke="white" strokeWidth="1.4" strokeLinecap="round" />
          </g>

          {/* Home marker (user) */}
          <g>
            <circle cx="200" cy="40" r="12" fill="#FF7B40" opacity="0.18" style={{ transformOrigin: "200px 40px", animation: "psHeroPulseHalo 2.6s ease-in-out infinite" }} />
            <circle cx="200" cy="40" r="8" fill="#FF7B40" />
            <path d="M196 42 v-4 l4 -4 l4 4 v4 z" fill="white" />
          </g>

          {/* Live status strip */}
          <g filter="url(#psCmpShadow)">
            <rect x="22" y="140" width="108" height="20" rx="10" fill="white" />
            <circle cx="34" cy="150" r="3.5" fill="#10B981">
              <animate attributeName="opacity" values="1;0.3;1" dur="1.2s" repeatCount="indefinite" />
            </circle>
            <text x="44" y="154" fill="#0F172A" fontSize="9" fontWeight="800" fontFamily="Inter, sans-serif">Live stock · 5 pharmacies</text>
          </g>
        </svg>
      </Box>
    );
  }

  // Step 3 — Delivery
  return (
    <Box {...baseWrap}>
      <svg width="100%" height="100%" viewBox="0 0 240 180" fill="none">
        <defs>
          <linearGradient id="psDelRoute" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#0D9E8C" />
            <stop offset="1" stopColor="#63D4C4" />
          </linearGradient>
          <filter id="psDelShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#0F172A" floodOpacity="0.18" />
          </filter>
        </defs>

        {/* Soft backdrop */}
        <ellipse cx="120" cy="158" rx="104" ry="8" fill="rgba(13,158,140,0.10)" />

        {/* Route track */}
        <path d="M 24 140 Q 120 108 216 146" stroke="rgba(15,23,42,0.08)" strokeWidth="8" strokeLinecap="round" fill="none" />
        <path d="M 24 140 Q 120 108 216 146" stroke="url(#psDelRoute)" strokeWidth="3" strokeLinecap="round" strokeDasharray="6 8" fill="none" style={{ animation: "psHeroDashFlow 1.8s linear infinite" }} />

        {/* Origin pharmacy pin */}
        <g>
          <circle cx="24" cy="140" r="12" fill="#0D9E8C" opacity="0.22" style={{ transformOrigin: "24px 140px", animation: "psHeroPulseHalo 2.4s ease-in-out infinite" }} />
          <circle cx="24" cy="140" r="8" fill="#0D9E8C" />
          <path d="M20 140 h8 M24 136 v8" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
        </g>

        {/* Destination home */}
        <g transform="translate(200 120)">
          <circle cx="16" cy="24" r="14" fill="#FF7B40" opacity="0.18" style={{ transformOrigin: "16px 24px", animation: "psHeroPulseHalo 2.4s ease-in-out 1s infinite" }} />
          <polygon points="16,6 32,22 0,22" fill="#DFF7F3" />
          <rect x="4" y="22" width="24" height="16" rx="2" fill="white" stroke="#99F6E4" strokeWidth="1.2" />
          <rect x="12" y="28" width="8" height="10" rx="1.5" fill="#0D9E8C" />
        </g>

        {/* Delivery scooter — moves along the route */}
        <g style={{ animation: "psHeroScooter 5s ease-in-out infinite", transformOrigin: "60px 124px" }} filter="url(#psDelShadow)">
          <g style={{ animation: "psHeroBobble 0.9s ease-in-out infinite" }}>
            {/* Body */}
            <path d="M 54 128 Q 56 114 70 114 L 98 114 Q 108 114 110 124 L 112 132 L 54 132 Z" fill="#0D9E8C" />
            <path d="M 60 116 L 66 104 L 82 104 L 82 116 Z" fill="#14B8A6" />
            {/* Delivery box */}
            <rect x="76" y="92" width="22" height="20" rx="4" fill="white" stroke="#0D9E8C" strokeWidth="1.5" />
            <path d="M83 102 h8 M87 98 v8" stroke="#0D9E8C" strokeWidth="2" strokeLinecap="round" />
            {/* Handlebar */}
            <path d="M100 116 L 106 100" stroke="#0B8677" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M102 100 L 112 100" stroke="#0B8677" strokeWidth="2.5" strokeLinecap="round" />
            {/* Rider */}
            <circle cx="96" cy="98" r="5" fill="#334155" />
            <rect x="92" y="95" width="8" height="2.5" fill="#94A3B8" />
            {/* Wheels */}
            <circle cx="62" cy="134" r="7" fill="#0F172A" stroke="white" strokeWidth="1.2" />
            <circle cx="62" cy="134" r="2.2" fill="#64748B" />
            <circle cx="100" cy="134" r="7" fill="#0F172A" stroke="white" strokeWidth="1.2" />
            <circle cx="100" cy="134" r="2.2" fill="#64748B" />
            {/* Speed lines */}
            <line x1="42" y1="118" x2="50" y2="118" stroke="#99F6E4" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
            <line x1="38" y1="124" x2="48" y2="124" stroke="#99F6E4" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
          </g>
        </g>

        {/* Sparkles */}
        <text x="150" y="40" fontSize="11" fill="#34D399" style={{ animation: "psHeroBadgePulse 1.8s ease-in-out infinite" }}>✦</text>
        <text x="60" y="36" fontSize="9" fill="#A78BFA" style={{ animation: "psHeroBadgePulse 2.2s ease-in-out .5s infinite" }}>✦</text>
      </svg>
    </Box>
  );
};

const HeroFlowCarousel = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((current) => (current + 1) % HERO_FLOW_STEPS.length);
    }, 3200);
    return () => clearInterval(timer);
  }, []);

  const step = HERO_FLOW_STEPS[active];

  return (
    <Box
      bg="rgba(255,255,255,0.55)"
      border={`1px solid ${colors.brand[100]}`}
      borderRadius="22px"
      px={5}
      py={4}
      minH="372px"
      boxShadow="0 14px 30px rgba(13,158,140,0.08)"
      backdropFilter="blur(8px)"
    >
      <style>{`
        @keyframes psHeroSlideIn {
          from { opacity: 0; transform: translateX(12px) translateY(4px); }
          to { opacity: 1; transform: translateX(0) translateY(0); }
        }
        @keyframes psHeroFloatSoft {
          0%,100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @keyframes psHeroDashFlow {
          from { stroke-dashoffset: 90; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes psHeroBadgePulse {
          0%,100% { transform: scale(1); opacity: .9; }
          50% { transform: scale(1.08); opacity: 1; }
        }
        @keyframes psHeroPulseHalo {
          0%,100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.08); opacity: .9; }
        }
        @keyframes psHeroPulseCore {
          0%,100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
        @keyframes psHeroScooter {
          0% { transform: translateX(0); }
          50% { transform: translateX(120px); }
          100% { transform: translateX(0); }
        }
        @keyframes psHeroRxBounce {
          0%,100% { transform: translateY(0px); }
          50% { transform: translateY(-3px); }
        }
        @keyframes psHeroWheel {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes psHeroScan {
          0%,100% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(68px); opacity: 1; }
        }
        @keyframes psHeroBobble {
          0%,100% { transform: translateY(0); }
          25% { transform: translateY(-1px); }
          75% { transform: translateY(-1px); }
        }
      `}</style>
      <Box key={active} style={{ animation: "psHeroSlideIn .35s ease-out" }}>
        <HeroScene step={active} />
        <Box minH="98px">
          <Text fontSize="11px" fontWeight="800" color={colors.brand[600]} textTransform="uppercase" letterSpacing="0.12em" m={0}>
            {step.eyebrow}
          </Text>
          <Text fontSize="16px" fontWeight="800" color={colors.neutral[900]} letterSpacing="-0.02em" mt={1} m={0} minH="44px">
            {step.title}
          </Text>
          <Text fontSize="12px" color={colors.neutral[500]} mt={1.5} m={0} lineHeight="1.6" minH="38px">
            {step.subtitle}
          </Text>
        </Box>
      </Box>
      <HStack spacing={2} mt={3}>
        {HERO_FLOW_STEPS.map((_, index) => (
          <Box
            key={index}
            as="button"
            aria-label={`Show hero step ${index + 1}`}
            onClick={() => setActive(index)}
            w={active === index ? "28px" : "8px"}
            h="8px"
            borderRadius="full"
            bg={active === index ? colors.brand[500] : colors.neutral[300]}
            transition="all .22s ease"
          />
        ))}
      </HStack>
    </Box>
  );
};

const hasCookie = (name) => {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .map((entry) => entry.trim())
    .some((entry) => entry.startsWith(`${name}=`));
};

const getGreeting = (date = new Date()) => {
  const h = date.getHours();
  if (h < 5) return "Good night";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Good night";
};

export const Home = () => {
  const navigate = useNavigate();
  const [cookies, , removeCookie] = useCookies(["token", "role"]);
  const isLoggedIn = Boolean(cookies.token || hasCookie("token"));
  const [username, setUsername] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [addressModalMode, setAddressModalMode] = useState("add");
  const [addressName, setAddressName] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const chakraToast = useToast();
  const [streetName, setStreetName] = useState("");
  const [buildingNumber, setBuildingNumber] = useState("");
  const [floor, setFloor] = useState("");
  const [apartment, setApartment] = useState("");
  const [fullUser, setFullUser] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(
    () => typeof window !== "undefined" && !localStorage.getItem("pillstack_onboarded"),
  );
  const [cartCount, setCartCount] = useState(0);
  const [cartItems, setCartItems] = useState([]);

  const GUEST_CART_KEY = "pillstack_guest_cart";
  const getGuestCart = () => {
    try {
      return JSON.parse(localStorage.getItem(GUEST_CART_KEY) || "[]");
    } catch {
      return [];
    }
  };
  const saveGuestCart = (items) => {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
  };
  const normalizeCartItem = (item) => ({
    productId: item.productId || item.product_id || item._id,
    name: item.name || item.product_name || item.Name || "Medicine",
    quantity: item.quantity || 1,
    price: item.price || item.mrp || item.Price || 0,
    manufacturer: item.manufacturer || item.manufacturer_name || "",
    pack_size: item.pack_size || item.packSize || "",
    composition: item.composition || item.Composition || "",
    image: item.image || item.Image || "",
  });
  const addToGuestCart = (item) => {
    const items = getGuestCart();
    const normalized = normalizeCartItem(item);
    const existing = items.find((i) => i.productId === normalized.productId);
    if (existing) {
      existing.quantity += normalized.quantity;
    } else {
      items.push(normalized);
    }
    saveGuestCart(items);
    setCartItems(items);
    setCartCount(items.reduce((s, i) => s + (i.quantity || 1), 0));
  };
  const updateGuestCart = (productId, newQty) => {
    let items = getGuestCart();
    if (newQty <= 0) {
      items = items.filter((i) => i.productId !== productId);
    } else {
      const item = items.find((i) => i.productId === productId);
      if (item) item.quantity = newQty;
    }
    saveGuestCart(items);
    setCartItems(items);
    setCartCount(items.reduce((s, i) => s + (i.quantity || 1), 0));
  };
  const [heroSearch, setHeroSearch] = useState("");
  const [heroResults, setHeroResults] = useState([]);
  const [heroSearching, setHeroSearching] = useState(false);
  const [heroAdding, setHeroAdding] = useState({});
  const heroDebounce = useRef(null);
  const heroSearchRef = useRef(null);
  const scanFileInputRef = useRef(null);
  const { isOpen: isScanOpen, onOpen: onScanOpen, onClose: onScanClose } = useDisclosure();
  const { isOpen: isRxOpen, onOpen: onRxOpen, onClose: onRxClose } = useDisclosure();
  const [rxOrders, setRxOrders] = useState([]);
  const [rxOrdersLoading, setRxOrdersLoading] = useState(false);

  const handleOpenRx = useCallback(async () => {
    onRxOpen();
    setRxOrdersLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/order`, { withCredentials: true });
      const orders = Array.isArray(data) ? data : (data?.orders || []);
      const rxOnly = orders
        .filter(o => o?.isPrescriptionOrder || (o?.prescriptionFiles?.length > 0))
        .sort((a, b) => new Date(b.createdAt || b.date_added || 0) - new Date(a.createdAt || a.date_added || 0));
      setRxOrders(rxOnly);
    } catch {
      setRxOrders([]);
    } finally {
      setRxOrdersLoading(false);
    }
  }, [onRxOpen]);

  const [reordering, setReordering] = useState({});

  const [scanFile, setScanFile] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanSuggestions, setScanSuggestions] = useState([]);
  const [scanError, setScanError] = useState("");
  const [scanAdding, setScanAdding] = useState({});
  const [scanAddedIds, setScanAddedIds] = useState(new Set());
  const [nearbyPharmacies, setNearbyPharmacies] = useState([]);
  const [searchRadiusKm, setSearchRadiusKm] = useState(5);
  const mapViewportRef = useRef(null);
  const [mapUserCoords, setMapUserCoords] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState(
    () => localStorage.getItem("pillstackLocation") || ""
  );
  const [pharmacyMatches, setPharmacyMatches] = useState([]);
  const [pharmacyMatchesLoading, setPharmacyMatchesLoading] = useState(false);
  const [pharmacyMatchesError, setPharmacyMatchesError] = useState("");
  const [selectedPharmacyId, setSelectedPharmacyId] = useState(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [ordersExpanded, setOrdersExpanded] = useState(true);
  const [, setTick] = useState(0);
  const { isOpen: isPriceOpen, onOpen: onPriceOpen, onClose: onPriceClose } = useDisclosure();
  const { isOpen: isLoginModalOpen, onOpen: onLoginModalOpen, onClose: onLoginModalClose } = useDisclosure();
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      if (heroSearchRef.current && !heroSearchRef.current.contains(e.target)) {
        setHeroResults([]);
        setHeroSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchCart = useCallback(async () => {
    if (!isLoggedIn) {
      const items = getGuestCart();
      setCartItems(items);
      setCartCount(items.reduce((s, i) => s + (i.quantity || 1), 0));
      return;
    }
    try {
      const resp = await axios.get(`${API_BASE}/cart`, { withCredentials: true });
      const items = resp.data?.items || [];
      setCartItems(items);
      setCartCount(items.reduce((s, i) => s + (i.quantity || 1), 0));
    } catch {
      setCartItems([]);
    }
  }, [isLoggedIn]);

  const handleRxReorder = useCallback(async (order) => {
    const items = order?.items || order?.Items || [];
    if (!items.length) {
      chakraToast({ title: "No medicines to reorder", status: "warning", duration: 2000, isClosable: true, position: "bottom-right" });
      return;
    }
    setReordering(prev => ({ ...prev, [order._id]: true }));
    let added = 0;
    for (const it of items) {
      if (!it.productId) continue;
      try {
        await axios.post(`${API_BASE}/cart`, { productId: it.productId, quantity: it.quantity || 1 }, { withCredentials: true });
        added++;
      } catch {}
    }
    setReordering(prev => ({ ...prev, [order._id]: false }));
    await fetchCart();
    if (added) {
      const rxFiles = order?.prescriptionFiles || [];
      if (rxFiles.length > 0) setPendingSavedPrescriptions(rxFiles);
      onRxClose();
      chakraToast({ title: `${added} medicine${added > 1 ? "s" : ""} added to cart`, status: "success", duration: 2000, isClosable: true, position: "bottom-right" });
    } else {
      chakraToast({ title: "Couldn't reorder", description: "Medicine IDs not found — add manually.", status: "error", duration: 2500, isClosable: true, position: "bottom-right" });
    }
  }, [fetchCart, onRxClose, chakraToast]);

  const routerLocation = useLocation();

  // Re-fetch cart on every navigation to this page (covers React Router in-SPA navigation)
  useEffect(() => {
    fetchCart();
  }, [fetchCart, routerLocation.key]);

  // Handle navigation state from navbar user menu
  const lastHandledNavKey = useRef(routerLocation.key);
  useEffect(() => {
    if (!routerLocation.state) return;
    if (lastHandledNavKey.current === routerLocation.key) return;
    lastHandledNavKey.current = routerLocation.key;
    if (routerLocation.state.openPrescriptions) {
      handleOpenRx();
    }
    if (routerLocation.state.openAddresses) {
      setAddressModalMode("list");
      setModalIsOpen(true);
    }
  }, [routerLocation.key, routerLocation.state, handleOpenRx]);

  // When user logs in, merge any guest cart items into the backend cart
  useEffect(() => {
    if (!isLoggedIn) return;
    const merge = async () => {
      const guestItems = getGuestCart();
      if (!guestItems.length) return;
      let merged = 0;
      for (const it of guestItems) {
        try {
          await axios.post(`${API_BASE}/cart`, { productId: it.productId, quantity: it.quantity || 1 }, { withCredentials: true });
          merged++;
        } catch {}
      }
      if (merged > 0) {
        saveGuestCart([]);
        await fetchCart();
      }
    };
    merge();
  }, [isLoggedIn]);

  // Re-fetch fullUser when a new address is saved from the navbar
  useEffect(() => {
    const onAddressSaved = async () => {
      if (!username) return;
      try {
        const { data } = await axios.get(`${API_BASE}/patient/myInfo/${username}`);
        setFullUser(data);
      } catch {}
    };
    window.addEventListener("pillstackAddressSaved", onAddressSaved);
    return () => window.removeEventListener("pillstackAddressSaved", onAddressSaved);
  }, [username]);

  const cartItemsRef = useRef(cartItems);
  useEffect(() => { cartItemsRef.current = cartItems; }, [cartItems]);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const runSearch = async () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const ac = new AbortController();
      abortControllerRef.current = ac;

      const addr = localStorage.getItem("pillstackLocation") || "";
      const rawCoords = localStorage.getItem("pillstackLocationCoords");
      setDeliveryAddress(addr);
      if (!addr || ac.signal.aborted) return;

      let coords = null;
      try { coords = rawCoords ? JSON.parse(rawCoords) : null; } catch {}
      if (!coords || ac.signal.aborted) return;

      setMapUserCoords(coords);

      // coords are set via setMapUserCoords above — PharmacyMap receives them via externalCoords prop
      // and handles fetching nearby pharmacies itself via /pharmacist/nearby
    };

    const onLocationChange = () => runSearch();

    runSearch(); // on mount: sync address+coords into state
    window.addEventListener("pillstackLocationChanged", onLocationChange);
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      window.removeEventListener("pillstackLocationChanged", onLocationChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchRadiusKm]);

  const handleScanFile = useCallback(async (file) => {
    if (!file) return;
    setScanFile(file);
    setScannedPrescriptionFile(file);
    setScanSuggestions([]);
    setScanError("");
    setScanAddedIds(new Set());
    setScanLoading(true);
    try {
      const form = new FormData();
      form.append("prescriptions", file);
      const { data } = await axios.post(`${API_BASE}/order/prescription/extract`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScanSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
      if (!data?.suggestions?.length) setScanError("No medicines detected. Try a clearer image.");
    } catch (err) {
      setScanError(err?.response?.data?.message || "Could not scan the prescription. Please try again.");
    } finally {
      setScanLoading(false);
    }
  }, []);

  const handleCheckPrices = useCallback(async (skipAuthCheck = false) => {
    const latestCartItems = cartItemsRef.current;
    console.log('[checkPrices] loggedIn=', isLoggedIn, 'skipAuth=', skipAuthCheck, 'cartCount=', latestCartItems.length, 'radius=', searchRadiusKm);
    if (!latestCartItems.length) {
      chakraToast({ title: "Cart is empty", description: "Add medicines before checking nearby prices.", status: "warning", duration: 2500, isClosable: true, position: "bottom-right" });
      return;
    }
    const rawCoords = localStorage.getItem("pillstackLocationCoords");
    let coords = null;
    try { coords = rawCoords ? JSON.parse(rawCoords) : null; } catch {}
    console.log('[checkPrices] coords=', coords, 'stored=', rawCoords);
    if (!coords) {
      chakraToast({ title: "Location not ready", description: "Please set your delivery address to check nearby pharmacies.", status: "warning", duration: 2800, isClosable: true, position: "bottom-right" });
      return;
    }
    if (!skipAuthCheck && !isLoggedIn) {
      setPendingAction('checkPrices');
      onLoginModalOpen();
      return;
    }
    const [lng, lat] = coords;
    const items = latestCartItems.map((i) => ({ name: i.name || "", quantity: i.quantity || 1, manufacturer: i.manufacturer || "", pack_size: i.pack_size || "", composition: i.composition || "" }));
    setPharmacyMatches([]);
    setPharmacyMatchesError("");
    setPharmacyMatchesLoading(true);
    onPriceOpen();
    try {
      const payload = { latitude: lat, longitude: lng, distanceRangeKm: searchRadiusKm, limit: 15, items };
      console.log('[checkPrices] payload=', payload);
      const { data } = await axios.post(
        `${API_BASE}/order/find-pharmacies`,
        payload,
        { withCredentials: true }
      );
      console.log('[checkPrices] response=', data);
      const matches = Array.isArray(data?.pharmacies) ? data.pharmacies : [];
      setPharmacyMatches(matches);
      if (!matches.length) setPharmacyMatchesError(data?.message || "No nearby pharmacies found.");
    } catch (err) {
      console.error('[checkPrices] error=', err);
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      if (status === 401 || message === 'Unauthorized') {
        setPendingAction('checkPrices');
        onLoginModalOpen();
        setPharmacyMatchesError('');
      } else {
        setPharmacyMatchesError(message || "Could not check pharmacies right now.");
      }
    } finally {
      setPharmacyMatchesLoading(false);
    }
  }, [searchRadiusKm, onPriceOpen, chakraToast, isLoggedIn, onLoginModalOpen]);


  const handleScanAdd = useCallback(async (med, idx) => {
    const key = med?.product_id || med?.extracted_name || String(idx);
    setScanAdding((prev) => ({ ...prev, [key]: true }));
    const productId = med?.product_id;
    try {
      if (!productId) return;
      if (!isLoggedIn) {
        addToGuestCart({ ...med, productId, quantity: 1 });
      } else {
        await axios.post(`${API_BASE}/cart`, { productId, quantity: 1 }, { withCredentials: true });
        await fetchCart();
      }
      setScanAddedIds((prev) => new Set([...prev, key]));
      toast.success(`${med?.product_name || med?.extracted_name || "Medicine"} added to cart`, { autoClose: 1500 });
    } catch (err) {
      const msg = err?.response?.data || "Failed to add";
      toast.error(typeof msg === "string" ? msg : "Failed to add", { autoClose: 2000 });
    } finally {
      setScanAdding((prev) => ({ ...prev, [key]: false }));
    }
  }, [fetchCart, isLoggedIn]);

  const initialRef = useRef(null);
  const finalRef = useRef(null);

  const handleHeroSearch = useCallback((val) => {
    setHeroSearch(val);
    if (heroDebounce.current) clearTimeout(heroDebounce.current);
    if (!val || val.trim().length < 2) {
      setHeroResults([]);
      setHeroSearching(false);
      return;
    }
    setHeroSearching(true);
    heroDebounce.current = setTimeout(async () => {
      try {
        const resp = await axios.get(`${API_BASE}/api/medicines/by-name`, {
          params: { name: val.trim(), limit: 8, namesOnly: false },
        });
        setHeroResults(resp.data?.results || []);
      } catch {
        setHeroResults([]);
      } finally {
        setHeroSearching(false);
      }
    }, 300);
  }, []);

  const handleHeroAdd = useCallback(async (med) => {
    const productId = med.product_id || med._id || med.id;
    if (!productId) return;
    setHeroAdding((prev) => ({ ...prev, [productId]: true }));
    try {
      if (!isLoggedIn) {
        addToGuestCart({ ...med, productId, quantity: 1 });
      } else {
        await axios.post(`${API_BASE}/cart`, { productId, quantity: 1 }, { withCredentials: true });
        await fetchCart();
      }
      toast.success(`${med.product_name || med.Name || med.name || "Medicine"} added to cart`, { autoClose: 1500 });
    } catch (err) {
      const msg = err?.response?.data || "Failed to add to cart";
      toast.error(typeof msg === "string" ? msg : "Failed to add to cart", { autoClose: 2000 });
    } finally {
      setHeroAdding((prev) => ({ ...prev, [productId]: false }));
    }
  }, [fetchCart, isLoggedIn]);

  const handleHeroQtyChange = useCallback(async (productId, newQty) => {
    setHeroAdding((prev) => ({ ...prev, [productId]: true }));
    try {
      if (!isLoggedIn) {
        updateGuestCart(productId, newQty);
      } else {
        if (newQty <= 0) {
          await axios.delete(`${API_BASE}/cart/${productId}`, { withCredentials: true });
        } else {
          await axios.post(`${API_BASE}/cart/update`, { productId, quantity: newQty }, { withCredentials: true });
        }
        await fetchCart();
      }
    } catch (err) {
      const msg = err?.response?.data || "Failed to update cart";
      toast.error(typeof msg === "string" ? msg : "Failed to update cart", { autoClose: 2000 });
    } finally {
      setHeroAdding((prev) => ({ ...prev, [productId]: false }));
    }
  }, [fetchCart, isLoggedIn]);

  useEffect(() => {
    const verifyCookie = async () => {
      if (!isLoggedIn) {
        setCheckingAuth(false);
        return;
      }
      let verifiedUser = null;
      try {
        const { data } = await axios.post(
          API_BASE,
          {},
          { withCredentials: true },
        );
        const { status, user } = data;
        if (status && user) {
          verifiedUser = user;
          setUsername(user);
        } else {
          removeCookie("token", { path: "/" });
        }
      } catch {
        // Token verification failed — clear cookie so UI shows Sign In cleanly
        removeCookie("token", { path: "/" });
      } finally {
        setCheckingAuth(false);
      }
      // Fetch user info and cart only after token is confirmed valid
      if (verifiedUser) {
        try {
          const fullUserData = await axios.get(`${API_BASE}/patient/myInfo/${verifiedUser}`);
          setFullUser(fullUserData.data);
        } catch { /* non-critical — profile info missing is ok */ }
        try {
          await fetchCart();
        } catch { /* non-critical — cart can retry */ }
      }
    };
    verifyCookie();
  }, [cookies, navigate, removeCookie, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchRecentOrders = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/order`, { withCredentials: true });
        const orders = Array.isArray(data) ? data : (data?.orders || []);
        setRecentOrders(orders.slice(0, 3));
      } catch {
        // silently fail — not critical
      }
    };
    fetchRecentOrders();
  }, [isLoggedIn, routerLocation.pathname]);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const timeAgo = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
    return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) > 1 ? "s" : ""} ago`;
  };

  const openChangePasswordModal = () => onOpen();
  const closeChangePasswordModal = () => onClose();
  const handleSidebarToggle = () => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setMobileSidebarOpen(true);
      return;
    }
    setSidebarCollapsed((prev) => !prev);
  };

  const Logout = () => performLogout(removeCookie);

  const addAddress = async (skipAuthCheck = false) => {
    try {
      if (!skipAuthCheck && !isLoggedIn) {
        setPendingAction('saveAddress');
        onLoginModalOpen();
        return;
      }
      if (
        !addressName.trim() ||
        !streetName.trim() ||
        !buildingNumber.trim() ||
        !floor.trim() ||
        !apartment.trim()
      ) {
        toast("Please fill in all fields", { position: "top-right" });
        return;
      }
      const newAddress = `${addressName},${streetName},${buildingNumber},${floor},${apartment}`.replaceAll(" ", "");
      const response = await axios.post(
        `${API_BASE}/patient/addDeliveryAddress`,
        { address: newAddress },
        { withCredentials: true },
      );
      if (response.data === "Delivery address added successfully") {
        toast("Delivery address added", { position: "top-right" });
        setAddressName("");
        setStreetName("");
        setBuildingNumber("");
        setFloor("");
        setApartment("");
        setFullUser((prev) => ({
          ...prev,
          DeliveryAddress: [...(prev?.DeliveryAddress || []), newAddress],
        }));
        setAddressModalMode("list");
      } else {
        toast("Could not add the delivery address", { position: "top-right" });
      }
    } catch (error) {
      toast("Internal server error", { position: "top-right" });
    }
  };

  const removeAddress = async (addressText) => {
    if (!isLoggedIn) return;
    try {
      await axios.delete(
        `${API_BASE}/patient/deliveryAddress`,
        { data: { address: addressText }, withCredentials: true },
      );
      setFullUser((prev) => ({
        ...prev,
        DeliveryAddress: (prev?.DeliveryAddress || []).filter((a) => {
          const text = typeof a === "string" ? a : (a?.address || "");
          return text !== addressText;
        }),
      }));
      toast("Address removed", { position: "top-right" });
    } catch {
      toast("Could not remove address", { position: "top-right" });
    }
  };

  const handleChangePass = async () => {
    const oldPassword = document.querySelector("#oldPassword").value;
    const newPassword = document.querySelector("#newPassword").value;
    const confirmNewPassword = document.querySelector("#confirmNewPassword").value;

    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords don't match", { position: "top-right" });
      return;
    }
    try {
      const response = await axios.post(
        `${API_BASE}/changePassword`,
        { oldPassword, newPassword },
        { withCredentials: true },
      );
      if (response.status === 201) {
        toast.success(response.data.message, { position: "top-right" });
      } else {
        toast.error(response.data.message, { position: "top-right" });
      }
    } catch (error) {
      if (error.response) toast.error(error.response.data.message, { position: "top-right" });
      else toast.error("An error occurred", { position: "top-right" });
    }
  };

  const firstName = useMemo(() => {
    if (fullUser?.Name) return fullUser.Name.split(" ")[0];
    if (username) return username;
    return "Guest";
  }, [fullUser, username]);

  const greeting = getGreeting();

  // Contextual health insight (placeholder — could be wired to real refill data)
  const refillHint = useMemo(() => {
    if (!isLoggedIn) {
      return "Sign in to save addresses, view past orders, and track deliveries";
    }
    const orders = fullUser?.Orders || [];
    const dueCount = Array.isArray(orders) ? Math.min(2, orders.length) : 0;
    if (dueCount > 0) {
      return `You have ${dueCount} medicine${dueCount > 1 ? "s" : ""} due for refill this week`;
    }
    return "Upload a prescription and get the best price from nearby pharmacies";
  }, [fullUser, isLoggedIn]);

  const statChips = [
    {
      icon: FiMapPin,
      eyebrow: "Network",
      label: "12 pharmacies near you",
      bg: colors.brand[50],
      fg: colors.brand[700],
      iconBg: colors.brand[100],
    },
    {
      icon: FiCheckCircle,
      eyebrow: "Scan Rx",
      label: "AI reads Rx in 3s",
      bg: colors.success[50],
      fg: colors.success[700],
      iconBg: colors.success[100],
    },
    {
      icon: FiZap,
      eyebrow: "Delivery",
      label: "45-min doorstep delivery",
      bg: colors.accent[50],
      fg: colors.accent[700],
      iconBg: colors.accent[100],
    },
  ];

  const activeOrders = useMemo(() => {
    if (!isLoggedIn) return [];
    const orders = Array.isArray(fullUser?.Orders) ? fullUser.Orders : [];
    return orders.filter((order) => {
      const status = String(order?.Status || order?.status || "").toLowerCase();
      return status && status !== "delivered" && status !== "cancelled";
    });
  }, [fullUser, isLoggedIn]);

  const quickReorders = useMemo(() => {
    if (!isLoggedIn) return [];
    const orders = Array.isArray(fullUser?.Orders) ? fullUser.Orders : [];
    return orders.slice(0, 3).map((order, index) => ({
      id: order?._id || `order-${index}`,
      title: order?.OrderName || order?.pharmacyName || `Prescription order ${index + 1}`,
      subtitle: order?.Status || order?.status || "Previously ordered",
      eta: order?.createdAt ? new Date(order.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "Recent",
    }));
  }, [fullUser, isLoggedIn]);

  const offerCards = [
    {
      title: "Flat 20% off on repeat prescriptions",
      copy: "Apply on your next refill order above ₹499 from licensed partner pharmacies.",
      eyebrow: "Offer",
      icon: FiPercent,
    },
    {
      title: "Free delivery on basket combos",
      copy: "Add everyday care products with your prescription medicines to unlock doorstep savings.",
      eyebrow: "Delivery",
      icon: FiTruck,
    },
    {
      title: "Generic savings spotlight",
      copy: "Compare equivalent salts and reduce monthly medicine spend before checkout.",
      eyebrow: "Savings",
      icon: FiRefreshCcw,
    },
  ];

  const categoryCards = [
    { title: "Diabetes Care", icon: FiDroplet, tone: colors.info[50], fg: colors.info[700] },
    { title: "Heart Health", icon: FiShield, tone: colors.error[50], fg: colors.error[700] },
    { title: "Vitamins", icon: FiSmile, tone: colors.warning[50], fg: colors.warning[700] },
    { title: "Skin & Hair", icon: FiUserCheck, tone: colors.brand[50], fg: colors.brand[700] },
    { title: "Diagnostics", icon: FiBookOpen, tone: colors.success[50], fg: colors.success[700] },
    { title: "Consult Doctor", icon: FiCreditCard, tone: colors.accent[50], fg: colors.accent[700] },
  ];

  return checkingAuth ? null : (
    <>
      <AnimStyles />
      {showOnboarding ? (
        <OnboardingOverlay
          onDismiss={() => {
            localStorage.setItem("pillstack_onboarded", "1");
            setShowOnboarding(false);
          }}
          onUploadRx={() => {
            localStorage.setItem("pillstack_onboarded", "1");
            setShowOnboarding(false);
          }}
        />
      ) : null}
      <Box position="fixed" top="0" left="0" right="0" zIndex="1000">
        <WithSubnavigation
          username={isLoggedIn ? username : ""}
          name={isLoggedIn ? fullUser?.Name : ""}
          openChangePasswordModal={isLoggedIn ? openChangePasswordModal : null}
          onLogout={isLoggedIn ? Logout : null}
          notifications={isLoggedIn ? fullUser?.Notifications : []}
          onToggleSidebar={handleSidebarToggle}
          collapsed={sidebarCollapsed}
          chatLink={isLoggedIn && username ? `/chatwithdoctor/${username}` : null}
          cartCount={cartCount}
          sidebarOffset="0px"
          savedAddresses={isLoggedIn ? fullUser?.DeliveryAddress || [] : []}
          isLoggedIn={isLoggedIn && Boolean(username)}
        />
      </Box>
      <Box
        mt="90px"
        minH="100vh"
        bg={colors.neutral[50]}
        ml={0}
        transition="margin-left 0.3s ease"
        pb={{ base: "88px", md: 0 }}
      >
        {/* hidden file input for prescription scan */}
        <input
          ref={scanFileInputRef}
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          style={{ display: "none" }}
          onChange={(e) => { if (e.target.files?.[0]) { handleScanFile(e.target.files[0]); onScanOpen(); } e.target.value = ""; }}
        />

        {/* Main layout: two-column top + full-width map below */}
        <Box px={{ base: "20px", md: "28px", xl: "36px" }} pt={6} pb={0}>
          <Box maxW="1560px" mx="auto">
            {/* Greeting row */}
            <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} gap={3} wrap="wrap" mb={5}>
              <Box minW={0}>
                <Text fontSize={{ base: "22px", md: "26px" }} fontWeight="900" color={colors.neutral[900]} m={0} letterSpacing="-0.02em" lineHeight="1.1">
                  {firstName ? `${greeting}, ${firstName}` : greeting}
                </Text>
                <Text fontSize="13px" color={colors.neutral[500]} mt={1} m={0}>
                  Search medicines or upload a prescription to get started.
                </Text>
              </Box>
              <HStack spacing={2} wrap="wrap">
                <Button variant="outline" borderColor={colors.brand[200]} color={colors.brand[700]} bg="white" size="sm" fontWeight="700" leftIcon={<FiPackage />} onClick={() => navigate("/orderdetails")}>Past Orders</Button>
                <Button variant="outline" borderColor={colors.brand[200]} color={colors.brand[700]} bg="white" size="sm" fontWeight="700" leftIcon={<FiFileText />} onClick={handleOpenRx}>Prescriptions</Button>
              </HStack>
            </Flex>

            {/* Recent orders — action alerts + compact chips */}
            {isLoggedIn && recentOrders.length > 0 && (() => {
              const enriched = recentOrders.map((o) => {
                const stage = o.workflowStage || (o.isPrescriptionOrder && o.status === "Processing" ? "review" : null);
                const isUserReview = stage === "pending_user_review";
                const isPayment = stage === "pending_payment" || stage === "payment_pending";
                const isUnderReview = stage === "review";
                const isDelivered = stage === "delivered" || o.status === "Delivered";
                const isInTransit = stage === "delivery_in_progress";
                const isOutForDelivery = stage === "delivery_created";
                const isReadyToPost = stage === "ready_to_post" || stage === "posted";
                const isInvoiced = o.status === "Invoice Generated";
                const isConfirmed = stage === "confirmed";
                const isRejected = stage === "rejected";
                const isCancelled = stage === "cancelled" || o.status === "Cancelled";
                const needsAction = isUserReview || isPayment;

                const accentColor = isRejected ? "#dc2626" : isCancelled ? "#dc2626" : isUserReview ? "#d97706" : isPayment ? "#1d4ed8" : isUnderReview ? "#059669"
                  : isDelivered ? "#4338ca" : isInTransit ? "#0891b2" : isOutForDelivery ? "#0284c7"
                  : isReadyToPost ? "#7c3aed" : isConfirmed ? "#16a34a" : colors.neutral[500];
                const bgColor = isRejected ? "#fef2f2" : isCancelled ? "#fef2f2" : isUserReview ? "#fffbeb" : isPayment ? "#eff6ff" : isUnderReview ? "#f0fdf4"
                  : isDelivered ? "#eef2ff" : isInTransit ? "#ecfeff" : isOutForDelivery ? "#eff6ff"
                  : isReadyToPost ? "#f5f3ff" : isConfirmed ? "#f0fdf4" : "white";
                const borderColor = isRejected ? "#fecaca" : isCancelled ? "#fecaca" : isUserReview ? "#fbbf24" : isPayment ? "#93c5fd" : isUnderReview ? "#6ee7b7"
                  : isDelivered ? "#c7d2fe" : isInTransit ? "#a5f3fc" : isOutForDelivery ? "#bae6fd"
                  : isReadyToPost ? "#ddd6fe" : isConfirmed ? "#bbf7d0" : colors.neutral[200];
                const statusLabel = isRejected ? "Rejected" : isCancelled ? "Cancelled" : isUserReview ? "Review changes" : isPayment ? "Payment due"
                  : isUnderReview ? "Under review" : isDelivered ? "Delivered"
                  : isInTransit ? "Out for delivery" : isOutForDelivery ? "Dispatched"
                  : isReadyToPost ? "Ready to dispatch" : isConfirmed ? "Confirmed"
                  : (o.status || "Processing");
                const actionIcon = isUserReview ? FiCheckCircle : isPayment ? FiCreditCard
                  : isInTransit || isOutForDelivery ? FiTruck : FiPackage;
                const elapsed = timeAgo(o.date_added || o.dateAdded);
                const dest = `/order/prescription/${o._id}`;
                return { o, stage, isUserReview, isPayment, isUnderReview, isDelivered, isInTransit, isOutForDelivery, isReadyToPost, isConfirmed, needsAction, accentColor, bgColor, borderColor, statusLabel, actionIcon, elapsed, dest };
              });

              const actionOrders = enriched.filter(e => e.needsAction);
              const otherOrders = enriched.filter(e => !e.needsAction);

              return (
                <Box mb={3}>
                  {/* Action-required banners */}
                  {actionOrders.map(({ o, isPayment, isUserReview, accentColor, bgColor, borderColor, statusLabel, actionIcon, elapsed, dest }) => (
                    <Box
                      key={o._id}
                      mb={2}
                      px={3} py={2.5}
                      borderRadius="14px"
                      border={`2px solid ${borderColor}`}
                      bg={bgColor}
                      cursor="pointer"
                      onClick={() => navigate(dest)}
                      _hover={{ opacity: 0.9 }}
                      position="relative"
                      overflow="hidden"
                    >
                      {/* Pulsing left accent bar */}
                      <Box
                        position="absolute" left={0} top={0} bottom={0} w="4px"
                        bg={accentColor}
                        borderLeftRadius="14px"
                      />
                      <Flex align="center" gap={3} pl={2}>
                        <Box
                          w="32px" h="32px" borderRadius="10px"
                          bg={accentColor}
                          display="flex" alignItems="center" justifyContent="center" flexShrink={0}
                          boxShadow={`0 0 0 4px ${accentColor}22`}
                        >
                          <Icon as={actionIcon} boxSize={3.5} color="white" />
                        </Box>
                        <Box flex={1} minW={0}>
                          <Flex align="center" gap={2}>
                            <Text fontWeight="800" fontSize="12px" color={colors.neutral[900]} m={0}>
                              #{String(o._id).slice(-8).toUpperCase()}
                            </Text>
                            <Box px={2} py="1px" borderRadius="full" bg={accentColor}>
                              <Text fontSize="10px" fontWeight="800" color="white" m={0}>⚡ {statusLabel}</Text>
                            </Box>
                          </Flex>
                          <Text fontSize="11px" color={accentColor} fontWeight="600" m={0}>
                            {isPayment ? "Tap to complete payment and confirm your order" : "Pharmacist updated your order — tap to review"}
                          </Text>
                        </Box>
                        <Icon as={isPayment ? FiCreditCard : FiCheckCircle} boxSize={4} color={accentColor} flexShrink={0} />
                      </Flex>
                    </Box>
                  ))}

                  {/* Non-action compact chips */}
                  {otherOrders.length > 0 && (
                    <>
                      <Flex align="center" justify="space-between" mb={1.5} mt={actionOrders.length > 0 ? 1 : 0}>
                        <Text fontSize="11px" fontWeight="800" color={colors.neutral[400]} textTransform="uppercase" letterSpacing="0.08em" m={0}>
                          Recent Orders
                        </Text>
                        <Button variant="link" size="xs" color={colors.brand[600]} fontWeight="700" onClick={() => navigate("/orderdetails")}>
                          View all
                        </Button>
                      </Flex>
                      <HStack spacing={2} overflowX="auto" pb={1} css={{ scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
                        {otherOrders.map(({ o, accentColor, bgColor, borderColor, statusLabel, elapsed, dest, isDelivered, isPaid }) => (
                          <Box
                            key={o._id}
                            px={3} py={2}
                            borderRadius="12px"
                            border={`1.5px solid ${borderColor}`}
                            bg={bgColor}
                            cursor="pointer"
                            onClick={() => navigate(dest)}
                            _hover={{ opacity: 0.85 }}
                            flexShrink={0}
                            minW="170px"
                          >
                            <Flex align="center" gap={2}>
                              <Box w="26px" h="26px" borderRadius="8px" bg={colors.neutral[100]} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                                <Icon as={isDelivered ? FiCheckCircle : FiPackage} boxSize={3} color={accentColor} />
                              </Box>
                              <Box minW={0}>
                                <Text fontWeight="800" fontSize="12px" color={colors.neutral[900]} m={0} noOfLines={1}>
                                  #{String(o._id).slice(-8).toUpperCase()}
                                </Text>
                                <Flex align="center" gap={1}>
                                  <Box px={1.5} py="1px" borderRadius="full" bg={colors.neutral[100]}>
                                    <Text fontSize="9px" fontWeight="700" color={accentColor} m={0}>{statusLabel}</Text>
                                  </Box>
                                  {elapsed && <Text fontSize="10px" color={colors.neutral[400]} m={0}>{elapsed}</Text>}
                                </Flex>
                              </Box>
                            </Flex>
                          </Box>
                        ))}
                      </HStack>
                    </>
                  )}

                  {/* If all are action orders, still show View all link */}
                  {otherOrders.length === 0 && actionOrders.length > 0 && (
                    <Flex justify="flex-end" mt={1}>
                      <Button variant="link" size="xs" color={colors.brand[600]} fontWeight="700" onClick={() => navigate("/orderdetails")}>
                        View all orders
                      </Button>
                    </Flex>
                  )}
                </Box>
              );
            })()}

            {/* Two-column: search+scan LEFT | cart RIGHT */}
            <Flex gap={6} align="flex-start" direction={{ base: "column", lg: "row" }}>

              {/* LEFT — search + scan */}
              <Box flex="1" minW={0}>
                <Box ref={heroSearchRef} position="relative">
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color={colors.neutral[400]} />
                </InputLeftElement>
                <Input
                  placeholder="Search medicines by brand or salt"
                  value={heroSearch}
                  onChange={(e) => handleHeroSearch(e.target.value)}
                  bg="white"
                  borderColor={colors.neutral[200]}
                  borderRadius={radii.md}
                  fontSize="15px"
                  _hover={{ borderColor: colors.brand[200] }}
                  _focus={{ borderColor: colors.brand[400], boxShadow: `0 0 0 1px ${colors.brand[400]}` }}
                />
              </InputGroup>
              {heroSearching && (
                <Box position="absolute" top="100%" left={0} right={0} mt={1} bg="white" border={`1px solid ${colors.neutral[200]}`} borderRadius={radii.md} boxShadow={shadows.md} p={3} zIndex={100}>
                  <Text fontSize="13px" color={colors.neutral[500]} m={0}>Searching...</Text>
                </Box>
              )}
              {!heroSearching && heroResults.length > 0 && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  mt={1}
                  bg="white"
                  border={`1px solid ${colors.neutral[200]}`}
                  borderRadius={radii.md}
                  boxShadow={shadows.lg}
                  maxH="360px"
                  overflowY="auto"
                  zIndex={100}
                >
                  <Stack spacing={0}>
                    {heroResults.map((med) => {
                      const name = med.product_name || med.Name || med.name || "Unnamed";
                      const comp = med.composition || med.key_ingredients || "";
                      const pack = med.pack_size_label || med.pack_size || med.packSize || "";
                      const mfr = med.manufacturer_name || med.Manufacturer || med.manufacturer || med.marketer || "";
                      const price = med.mrp != null ? Number(med.mrp).toFixed(2) : null;
                      const pid = med.product_id || med._id || med.id;
                      const busy = pid && heroAdding[pid];
                      const cartItem = pid ? cartItems.find(i => i.productId === pid) : null;
                      const inCart = !!cartItem;
                      const qty = cartItem?.quantity || 0;
                      return (
                        <Flex
                          key={pid || name}
                          align="center"
                          gap={3}
                          px={4}
                          py={3}
                          _hover={{ bg: colors.neutral[50] }}
                          transition="background .12s"
                          borderBottom={`1px solid ${colors.neutral[100]}`}
                        >
                          <Box flex="1" minW={0}>
                            <Text fontSize="14px" fontWeight="800" color={colors.neutral[900]} m={0} noOfLines={1}>{name}</Text>
                            {comp && <Text fontSize="12px" color={colors.neutral[500]} m={0} noOfLines={1}>{comp}</Text>}
                            {(pack || mfr) && <Text fontSize="11px" color={colors.neutral[400]} m={0} mt={0.5} noOfLines={1}>{[pack, mfr].filter(Boolean).join(" · ")}</Text>}
                          </Box>
                          {price && <Text fontSize="14px" fontWeight="800" color={colors.neutral[900]} m={0} flexShrink={0}>₹{price}</Text>}
                          {inCart ? (
                            <HStack spacing={1} flexShrink={0}>
                              <IconButton
                                icon={qty <= 1 ? <FiTrash2 /> : <FiMinus />}
                                size="xs"
                                borderRadius="full"
                                colorScheme={qty <= 1 ? "red" : "gray"}
                                variant="outline"
                                aria-label="Decrease"
                                isLoading={busy}
                                onClick={() => handleHeroQtyChange(pid, qty - 1)}
                              />
                              <Text fontSize="14px" fontWeight="800" color={colors.neutral[900]} m={0} minW="20px" textAlign="center">{qty}</Text>
                              <IconButton
                                icon={<FiPlus />}
                                size="xs"
                                borderRadius="full"
                                bgGradient={gradients.brand}
                                color="white"
                                _hover={{ bgGradient: gradients.brandBold }}
                                aria-label="Increase"
                                isLoading={busy}
                                onClick={() => handleHeroQtyChange(pid, qty + 1)}
                              />
                            </HStack>
                          ) : (
                            <Button
                              size="sm"
                              bgGradient={gradients.brand}
                              color="white"
                              leftIcon={<FiPlus />}
                              borderRadius="full"
                              px={4}
                              flexShrink={0}
                              _hover={{ bgGradient: gradients.brandBold }}
                              onClick={() => handleHeroAdd(med)}
                              isLoading={busy}
                              loadingText="Adding"
                            >
                              Add
                            </Button>
                          )}
                        </Flex>
                      );
                    })}
                  </Stack>
                </Box>
              )}
              {!heroSearching && heroSearch.length >= 2 && heroResults.length === 0 && (
                <Box position="absolute" top="100%" left={0} right={0} mt={1} bg="white" border={`1px solid ${colors.neutral[200]}`} borderRadius={radii.md} boxShadow={shadows.md} p={3} zIndex={100}>
                  <Text fontSize="13px" fontWeight="700" color={colors.neutral[900]} m={0}>No matches found</Text>
                  <Text fontSize="12px" color={colors.neutral[500]} m={0}>Try a different spelling</Text>
                </Box>
              )}
            </Box>
            {/* OR — prescription scan card */}
            <Flex align="center" gap={3} mt={4} mb={3} mx="auto" maxW="720px">
              <Divider borderColor={colors.neutral[200]} />
              <Text fontSize="12px" fontWeight="700" color={colors.neutral[400]} flexShrink={0} m={0}>OR</Text>
              <Divider borderColor={colors.neutral[200]} />
            </Flex>
            {scanSuggestions.length > 0 ? (
              /* ── Results exist: show summary card ── */
              <Box mx="auto" maxW="720px" bg={colors.brand[50]} border={`1.5px solid ${colors.brand[200]}`} borderRadius="16px" overflow="hidden">
                {/* Top bar */}
                <Flex align="center" justify="space-between" px={5} py={3} borderBottom={`1px solid ${colors.brand[100]}`}>
                  <Flex align="center" gap={2}>
                    <Icon as={FiCheckCircle} color={colors.brand[600]} boxSize={4} />
                    <Text fontWeight="800" fontSize="13px" color={colors.brand[700]} m={0}>
                      Prescription scanned — {scanSuggestions.length} medicine{scanSuggestions.length > 1 ? "s" : ""} detected
                    </Text>
                  </Flex>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    fontWeight="700"
                    onClick={() => { setScanSuggestions([]); setScanError(""); setScanFile(null); setScanAddedIds(new Set()); clearScannedPrescriptionFile(); }}
                  >
                    Clear
                  </Button>
                </Flex>
                {/* Medicine preview chips */}
                <Box px={5} py={3} overflowX="auto">
                  <HStack spacing={2} minW="max-content">
                    {scanSuggestions.slice(0, 6).map((med, idx) => {
                      const pid = med?.product_id;
                      const inCart = pid ? cartItems.some(i => i.productId === pid) : false;
                      return (
                        <Flex key={idx} align="center" gap={1.5} px={3} py={1.5} bg="white" border={`1px solid ${inCart ? colors.brand[300] : colors.neutral[200]}`} borderRadius="full" flexShrink={0}>
                          {inCart && <Icon as={FiCheckCircle} boxSize={3} color={colors.brand[500]} />}
                          <Text fontSize="12px" fontWeight="700" color={colors.neutral[800]} m={0}>{med?.product_name || med?.extracted_name || "Medicine"}</Text>
                        </Flex>
                      );
                    })}
                    {scanSuggestions.length > 6 && (
                      <Box px={3} py={1.5} bg="white" border={`1px solid ${colors.neutral[200]}`} borderRadius="full" flexShrink={0}>
                        <Text fontSize="12px" fontWeight="700" color={colors.neutral[500]} m={0}>+{scanSuggestions.length - 6} more</Text>
                      </Box>
                    )}
                  </HStack>
                </Box>
                {/* Action row */}
                <Flex align="center" gap={3} px={5} py={3} borderTop={`1px solid ${colors.brand[100]}`}>
                  <Button
                    size="sm"
                    bgGradient={gradients.brand}
                    color="white"
                    borderRadius="full"
                    px={5}
                    fontWeight="700"
                    _hover={{ bgGradient: gradients.brandBold }}
                    onClick={onScanOpen}
                  >
                    View &amp; Add Medicines
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    color={colors.neutral[600]}
                    borderRadius="full"
                    fontWeight="700"
                    leftIcon={<FiCamera />}
                    onClick={() => scanFileInputRef.current?.click()}
                  >
                    Scan another
                  </Button>
                </Flex>
              </Box>
            ) : (
              /* ── No results: upload prompt card ── */
              <Flex
                mx="auto"
                maxW="720px"
                align="center"
                gap={4}
                px={5}
                py={4}
                bg="white"
                border={`1.5px dashed ${colors.brand[300]}`}
                borderRadius="16px"
                cursor="pointer"
                _hover={{ bg: colors.brand[50], borderColor: colors.brand[400] }}
                transition="all .15s"
                onClick={() => scanFileInputRef.current?.click()}
              >
                <Box w="44px" h="44px" borderRadius="12px" bg={colors.brand[50]} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                  <Icon as={FiCamera} color={colors.brand[600]} boxSize={5} />
                </Box>
                <Box flex={1} minW={0}>
                  <Text fontWeight="800" fontSize="14px" color={colors.neutral[900]} m={0}>Have a prescription?</Text>
                  <Text fontSize="12px" color={colors.neutral[500]} m={0} mt={0.5}>
                    Upload a photo or PDF — our AI will scan and detect all medicines automatically.
                  </Text>
                </Box>
                <Button
                  size="sm"
                  bgGradient={gradients.brand}
                  color="white"
                  borderRadius="full"
                  px={5}
                  fontWeight="700"
                  flexShrink={0}
                  _hover={{ bgGradient: gradients.brandBold }}
                  onClick={(e) => { e.stopPropagation(); scanFileInputRef.current?.click(); }}
                >
                  Scan
                </Button>
              </Flex>
            )}{/* end scan card */}
              </Box>{/* end LEFT column */}

              {/* RIGHT — cart panel */}
              <Box
                w={{ base: "100%", lg: "340px" }}
                flexShrink={0}
                bg="white"
                border={`1px solid ${colors.neutral[200]}`}
                borderRadius="16px"
                boxShadow={shadows.md}
                overflow="hidden"
              >
                <Flex align="center" justify="space-between" px={4} pt={4} pb={3} borderBottom={`1px solid ${colors.neutral[100]}`}>
                  <Text fontWeight="800" fontSize="15px" color={colors.neutral[900]} m={0}>Your Cart</Text>
                  {cartItems.length > 0 && (
                    <Badge colorScheme="teal" borderRadius="full" px={2}>{cartItems.reduce((s, i) => s + (i.quantity || 1), 0)} items</Badge>
                  )}
                </Flex>
                {cartItems.length === 0 ? (
                  <VStack spacing={2} py={10} px={4} textAlign="center">
                    <Icon as={FiPackage} boxSize={8} color={colors.neutral[300]} />
                    <Text fontSize="13px" fontWeight="700" color={colors.neutral[400]} m={0}>Your cart is empty</Text>
                    <Text fontSize="12px" color={colors.neutral[400]} m={0}>Search and add medicines to get started</Text>
                  </VStack>
                ) : (
                  <Flex direction="column" maxH="420px">
                    <Box flex={1} overflowY="auto">
                      <Stack spacing={0}>
                        {cartItems.map((item) => {
                          const pid = item.productId;
                          const busy = heroAdding[pid];
                          const qty = item.quantity || 1;
                          return (
                            <Flex key={pid} align="center" gap={3} px={4} py={3} borderBottom={`1px solid ${colors.neutral[50]}`} _hover={{ bg: colors.neutral[50] }}>
                              <Box flex={1} minW={0}>
                                <Text fontSize="13px" fontWeight="700" color={colors.neutral[900]} m={0} noOfLines={1}>{item.name || "Medicine"}</Text>
                                {item.price > 0 && <Text fontSize="11px" color={colors.neutral[500]} m={0}>₹{Number(item.price).toFixed(2)} each</Text>}
                              </Box>
                              <HStack spacing={1} flexShrink={0}>
                                <IconButton
                                  icon={qty <= 1 ? <FiTrash2 /> : <FiMinus />}
                                  size="xs" borderRadius="full"
                                  colorScheme={qty <= 1 ? "red" : "gray"}
                                  variant="outline"
                                  aria-label="Decrease"
                                  isLoading={busy}
                                  onClick={() => handleHeroQtyChange(pid, qty - 1)}
                                />
                                <Text fontSize="13px" fontWeight="800" color={colors.neutral[900]} m={0} minW="20px" textAlign="center">{qty}</Text>
                                <IconButton
                                  icon={<FiPlus />}
                                  size="xs" borderRadius="full"
                                  bgGradient={gradients.brand}
                                  color="white"
                                  _hover={{ bgGradient: gradients.brandBold }}
                                  aria-label="Increase"
                                  isLoading={busy}
                                  onClick={() => handleHeroQtyChange(pid, qty + 1)}
                                />
                              </HStack>
                            </Flex>
                          );
                        })}
                      </Stack>
                    </Box>
                    <Box px={4} py={3} bg={colors.neutral[50]} borderTop={`1px solid ${colors.neutral[100]}`} flexShrink={0}>
                      <Flex justify="space-between" align="center">
                        <Text fontSize="13px" fontWeight="800" color={colors.neutral[700]} m={0}>Total</Text>
                        <Text fontSize="14px" fontWeight="900" color={colors.brand[700]} m={0}>
                          ₹{cartItems.reduce((s, i) => s + (Number(i.price) || 0) * (i.quantity || 1), 0).toFixed(2)}
                        </Text>
                      </Flex>
                      <Button
                        mt={3} w="100%" size="sm"
                        bgGradient={gradients.brand}
                        color="white" borderRadius="full" fontWeight="700"
                        _hover={{ bgGradient: gradients.brandBold }}
                        leftIcon={<FiMapPin />}
                        isLoading={pharmacyMatchesLoading}
                        loadingText="Checking…"
                        onClick={() => handleCheckPrices()}
                      >
                        Check Prices &amp; Nearby Availability
                      </Button>
                    </Box>
                  </Flex>
                )}
              </Box>{/* end RIGHT column */}

            </Flex>{/* end two-column Flex */}
          </Box>{/* end maxW container */}
        </Box>{/* end px wrapper */}

        {/* Nearby pharmacies — map LEFT, list RIGHT */}
        <Box px={{ base: "20px", md: "28px", xl: "36px" }} pt={6} pb={10}>
          <Box maxW="1560px" mx="auto">
            {/* Section header */}
            <Flex align="center" justify="space-between" mb={4} wrap="wrap" gap={3}>
              <Box>
                <Text fontWeight="900" fontSize="20px" color={colors.neutral[900]} m={0} letterSpacing="-0.01em">Nearby Pharmacies</Text>
                <Text fontSize="13px" color={colors.neutral[500]} m={0} mt={0.5}>See coverage near you. Compare live stock once your list is ready.</Text>
              </Box>
              <Flex align="center" gap={3}>
                <Text fontSize="12px" fontWeight="700" color={colors.neutral[500]} m={0}>Radius: {searchRadiusKm} km</Text>
                <Box
                  as="input" type="range" min={3} max={20} step={1}
                  value={searchRadiusKm}
                  onChange={(e) => setSearchRadiusKm(Number(e.target.value))}
                  w="110px" cursor="pointer"
                />
                <Badge colorScheme="teal" borderRadius="full" px={3} py={1}>{nearbyPharmacies.length} found</Badge>
              </Flex>
            </Flex>

            {/* Two-column body */}
            <Flex gap={5} align="stretch" direction={{ base: "column", lg: "row" }}>

              {/* LEFT — Map */}
              <Box
                flex="1"
                minW={0}
                borderRadius="20px"
                overflow="hidden"
                bg="white"
                border={`1px solid ${colors.neutral[200]}`}
                boxShadow={shadows.md}
                h={{ base: "380px", md: "480px", lg: "560px" }}
                position="relative"
              >
                <Box position="absolute" inset={0}>
                  <PharmacyMap
                    height="100%"
                    isLoggedIn={true}
                    embeddedMapOnly
                    hideSearchOverlay
                    hideDeliveryBubble
                    searchRadiusKm={searchRadiusKm}
                    externalCoords={mapUserCoords}
                    skipGpsOnMount={!!localStorage.getItem("pillstackLocation")}
                    onGpsLocation={({ coords }) => {
                      if (coords) {
                        localStorage.setItem("pillstackLocationCoords", JSON.stringify(coords));
                        setMapUserCoords(coords);
                      }
                    }}
                    onLocationChange={({ address, coords }) => {
                      if (!localStorage.getItem("pillstackLocation") && address) {
                        localStorage.setItem("pillstackLocation", address);
                        if (coords) localStorage.setItem("pillstackLocationCoords", JSON.stringify(coords));
                        setDeliveryAddress(address);
                        window.dispatchEvent(new Event("pillstackLocationChanged"));
                      }
                    }}
                    onPharmaciesChange={(pharmacies) => {
                      setNearbyPharmacies((pharmacies || []).map((p) => ({
                        id: p.id,
                        name: p.name,
                        address: p.fullAddress || p.locality || "",
                        coords: p.coords || null,
                        distanceKm: p.distance ? parseFloat(String(p.distance).replace(/[^\d.]/g, "")) || null : null,
                        isOpen: Boolean(p.isOpen),
                        openStatus: p.openStatus || "",
                      })));
                    }}
                  />
                </Box>
              </Box>

              {/* RIGHT — Pharmacy list */}
              <Box
                w={{ base: "100%", lg: "340px" }}
                flexShrink={0}
                bg="white"
                border={`1px solid ${colors.neutral[200]}`}
                borderRadius="20px"
                boxShadow={shadows.md}
                overflow="hidden"
                display="flex"
                flexDirection="column"
              >
                {/* List header */}
                <Box px={5} pt={4} pb={3} borderBottom={`1px solid ${colors.neutral[100]}`}>
                  <Text fontWeight="800" fontSize="14px" color={colors.neutral[900]} m={0}>
                    {nearbyPharmacies.length > 0 ? `${nearbyPharmacies.length} pharmacies found` : "Finding pharmacies near you…"}
                  </Text>
                </Box>

                {/* List body */}
                <Stack
                  spacing={0}
                  flex={1}
                  overflowY="auto"
                  maxH={{ base: "340px", lg: "none" }}
                  sx={{ "&::-webkit-scrollbar": { width: "4px" }, "&::-webkit-scrollbar-thumb": { background: colors.neutral[200], borderRadius: "999px" } }}
                >
                  {nearbyPharmacies.length === 0 ? (
                    <VStack spacing={2} py={10} px={4} textAlign="center">
                      <Icon as={FiMapPin} boxSize={8} color={colors.neutral[300]} />
                      <Text fontSize="13px" fontWeight="700" color={colors.neutral[400]} m={0}>Locating nearby pharmacies…</Text>
                      <Text fontSize="12px" color={colors.neutral[400]} m={0}>Allow location access for accurate results</Text>
                    </VStack>
                  ) : (
                    nearbyPharmacies.map((p, idx) => (
                      <Flex
                        key={p.id}
                        align="flex-start"
                        gap={3}
                        px={5}
                        py={3.5}
                        borderBottom={`1px solid ${colors.neutral[50]}`}
                        _hover={{ bg: colors.neutral[50] }}
                        transition="background .12s"
                      >
                        {/* Index badge */}
                        <Flex
                          w="28px" h="28px" flexShrink={0}
                          borderRadius="full"
                          bg={idx === 0 ? colors.brand[500] : colors.neutral[200]}
                          color={idx === 0 ? "white" : colors.neutral[600]}
                          align="center" justify="center"
                          fontSize="11px" fontWeight="900"
                          mt="1px"
                        >
                          {idx + 1}
                        </Flex>

                        <Box flex={1} minW={0}>
                          <Text fontSize="13px" fontWeight="800" color={colors.neutral[900]} m={0} noOfLines={1}>{p.name}</Text>
                          {p.address && (
                            <Text fontSize="11px" color={colors.neutral[500]} m={0} mt="2px" noOfLines={1}>{p.address}</Text>
                          )}
                          <HStack spacing={2} mt={1.5} wrap="wrap">
                            {p.distanceKm != null && (
                              <Box as="span" fontSize="10px" fontWeight="700" color={colors.brand[700]} bg={colors.brand[50]} px={2} py="2px" borderRadius="full">
                                {Number(p.distanceKm).toFixed(1)} km
                              </Box>
                            )}
                            <Box
                              as="span" fontSize="10px" fontWeight="700"
                              color={p.isOpen ? "#166534" : "#92400e"}
                              bg={p.isOpen ? "#dcfce7" : "#fef3c7"}
                              px={2} py="2px" borderRadius="full"
                            >
                              {p.isOpen ? "Open" : "Closed"}
                            </Box>
                          </HStack>
                        </Box>
                      </Flex>
                    ))
                  )}
                </Stack>
              </Box>

            </Flex>
          </Box>
        </Box>

        {/* Pharmacy price-check results Drawer */}
        <Drawer isOpen={isPriceOpen} onClose={onPriceClose} placement="right" size="lg">
          <DrawerOverlay bg="blackAlpha.600" />
          <DrawerContent borderLeftRadius="20px">
            <DrawerCloseButton top={4} />
            <DrawerHeader borderBottom={`1px solid ${colors.neutral[100]}`} pb={4}>
              <Flex align="center" gap={3}>
                <Box w="36px" h="36px" borderRadius="10px" bg={colors.brand[50]} display="flex" alignItems="center" justifyContent="center">
                  <Icon as={FiMapPin} color={colors.brand[600]} boxSize={4} />
                </Box>
                <Box>
                  <Text m={0} fontWeight="800" fontSize="16px">Nearby Pharmacy Prices</Text>
                  <Text m={0} fontSize="12px" color={colors.neutral[500]} fontWeight="400">
                    Live stock &amp; pricing for {cartItems.length} medicine{cartItems.length !== 1 ? "s" : ""} in your cart
                  </Text>
                </Box>
              </Flex>
            </DrawerHeader>
            <DrawerBody py={5} px={4}>
              {pharmacyMatchesLoading && (
                <Flex direction="column" align="center" gap={3} py={12}>
                  <Spinner size="lg" color={colors.brand[500]} thickness="3px" />
                  <Text fontWeight="700" fontSize="14px" color={colors.neutral[700]} m={0}>Checking nearby stores…</Text>
                  <Text fontSize="12px" color={colors.neutral[500]} m={0}>Querying live stock from central server</Text>
                </Flex>
              )}
              {!pharmacyMatchesLoading && pharmacyMatchesError && (
                <Flex direction="column" align="center" gap={3} py={12} textAlign="center">
                  <Icon as={FiMapPin} boxSize={10} color={colors.neutral[300]} />
                  <Text fontWeight="700" fontSize="14px" color={colors.neutral[700]} m={0}>{pharmacyMatchesError}</Text>
                  <Button size="sm" variant="outline" borderRadius="full" onClick={() => handleCheckPrices()}>Retry</Button>
                </Flex>
              )}
              {!pharmacyMatchesLoading && pharmacyMatches.length > 0 && (
                <Stack spacing={4}>
                  <Text fontSize="12px" fontWeight="700" color={colors.neutral[500]} m={0}>
                    {pharmacyMatches.length} store{pharmacyMatches.length !== 1 ? "s" : ""} found within {searchRadiusKm} km — sorted by best match
                  </Text>
                  <PharmacyOfferCards
                    pharmacies={pharmacyMatches}
                    requestedItems={cartItems}
                    selectedPharmacyId={selectedPharmacyId}
                    onChoose={(p) => {
                      setSelectedPharmacyId(p.pharmacyId);
                      setSelectedPharmacy(p);
                    }}
                  />
                </Stack>
              )}
            </DrawerBody>
            {selectedPharmacy && (
              <DrawerFooter borderTop="1px solid" borderColor="gray.100" bg="white" px={4} py={3}>
                <Flex w="full" align="center" justify="space-between" gap={3}>
                  <Box minW={0}>
                    <Text fontSize="11px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.06em" m={0}>Selected</Text>
                    <Text fontSize="13px" fontWeight="800" color="gray.800" noOfLines={1} m={0}>{selectedPharmacy.name}</Text>
                    {selectedPharmacy.address && (
                      <Text fontSize="11px" color="gray.500" noOfLines={1} m={0}>{selectedPharmacy.address}</Text>
                    )}
                  </Box>
                  <Button
                    size="md"
                    h="42px"
                    px={5}
                    bg="linear-gradient(135deg, #0f766e 0%, #0d9488 100%)"
                    color="white"
                    _hover={{ bg: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)", transform: "translateY(-1px)", boxShadow: "0 6px 20px rgba(15,118,110,0.3)" }}
                    _active={{ transform: "none" }}
                    borderRadius="10px"
                    fontWeight="700"
                    fontSize="sm"
                    flexShrink={0}
                    onClick={() => {
                      onPriceClose();
                      navigate("/order/checkout", {
                        state: {
                          pharmacy: {
                            pharmacyId: selectedPharmacy.pharmacyId,
                            name: selectedPharmacy.name,
                            address: selectedPharmacy.address,
                            location: selectedPharmacy.location,
                            delivery: selectedPharmacy.delivery,
                            distanceKm: selectedPharmacy.distanceKm,
                          },
                          cartItems,
                          pharmacyItems: selectedPharmacy.items || [],
                          deliveryAddress: deliveryAddress || (fullUser?.DeliveryAddress || [])[0]?.address || (fullUser?.DeliveryAddress || [])[0] || "",
                          deliveryCoords: mapUserCoords || null,
                        },
                      });
                    }}
                  >
                    Finalize Order
                  </Button>
                </Flex>
              </DrawerFooter>
            )}
          </DrawerContent>
        </Drawer>

        {/* Prescription History Modal */}
        <Modal isOpen={isRxOpen} onClose={onRxClose} size="xl" scrollBehavior="inside">
          <ModalOverlay bg="blackAlpha.600" />
          <ModalContent borderRadius="20px" mx={4}>
            <ModalHeader borderBottom={`1px solid ${colors.neutral[100]}`} pb={4}>
              <Flex align="center" gap={3}>
                <Box w="36px" h="36px" borderRadius="10px" bg={colors.brand[50]} display="flex" alignItems="center" justifyContent="center">
                  <Icon as={FiFileText} color={colors.brand[600]} boxSize={4} />
                </Box>
                <Box>
                  <Text m={0} fontWeight="800" fontSize="16px">My Prescriptions</Text>
                  <Text m={0} fontSize="12px" color={colors.neutral[500]} fontWeight="400">All prescription orders, newest first</Text>
                </Box>
              </Flex>
            </ModalHeader>
            <ModalCloseButton top={4} />
            <ModalBody py={5}>
              {rxOrdersLoading ? (
                <VStack py={10}><Spinner color="teal.500" /><Text fontSize="13px" color={colors.neutral[400]} m={0}>Loading prescriptions…</Text></VStack>
              ) : (() => {
                if (!rxOrders.length) {
                  return (
                    <VStack spacing={3} py={10} textAlign="center">
                      <Icon as={FiFileText} boxSize={10} color={colors.neutral[200]} />
                      <Text fontWeight="700" color={colors.neutral[400]} m={0}>No prescription orders yet</Text>
                      <Text fontSize="13px" color={colors.neutral[400]} m={0}>Scan a prescription or upload one when placing an order</Text>
                    </VStack>
                  );
                }
                return (
                  <Stack spacing={3}>
                    {rxOrders.map((order, idx) => {
                      const files = order?.prescriptionFiles || [];
                      const date = order.createdAt || order.date_added;
                      const dateStr = date ? new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Unknown date";
                      const status = order.workflowStage || order.Status || order.status || "Processing";
                      const statusColor = status === "Delivered" ? "#16a34a" : status === "Cancelled" ? "#dc2626" : "#0f766e";
                      return (
                        <Box key={order._id || idx} border={`1px solid ${colors.neutral[200]}`} borderRadius="14px" overflow="hidden">
                          {/* Header row */}
                          <Flex align="center" justify="space-between" px={4} py={3} bg={colors.neutral[50]} borderBottom={`1px solid ${colors.neutral[100]}`}>
                            <HStack spacing={2}>
                              <Icon as={FiCalendar} color={colors.neutral[400]} boxSize={3.5} />
                              <Text fontSize="12px" fontWeight="700" color={colors.neutral[600]} m={0}>{dateStr}</Text>
                              {order.pharmacyName && (
                                <Text fontSize="12px" color={colors.neutral[400]} m={0}>· {order.pharmacyName}</Text>
                              )}
                            </HStack>
                            <HStack spacing={2}>
                              <Box as="span" fontSize="10px" fontWeight="700" color={statusColor} bg={`${statusColor}14`} px={2} py="2px" borderRadius="full">{status}</Box>
                              <Button
                                size="xs" variant="ghost" color={colors.brand[600]} fontWeight="700" rightIcon={<FiExternalLink size={10} />}
                                onClick={() => { onRxClose(); navigate(`/order/prescription/${order._id}`); }}
                              >
                                View
                              </Button>
                              <Button
                                size="xs" colorScheme="teal" fontWeight="700" borderRadius="full"
                                leftIcon={<FiRefreshCcw size={10} />}
                                isLoading={reordering[order._id]}
                                onClick={() => handleRxReorder(order)}
                              >
                                Reorder
                              </Button>
                            </HStack>
                          </Flex>
                          {/* Files */}
                          <Box px={4} py={3}>
                            {files.length === 0 ? (
                              <Text fontSize="12px" color={colors.neutral[400]} m={0}>No prescription files attached</Text>
                            ) : (
                              <Stack spacing={2}>
                                {files.map((f, fi) => {
                                  const fileUrl = resolveFileUrl(f.path);
                                  const isImage = f.mimeType?.startsWith("image/");
                                  return (
                                    <Flex key={fi} align="center" gap={3} p={2} border={`1px solid ${colors.neutral[100]}`} borderRadius="10px" bg="white">
                                      {isImage ? (
                                        <Box
                                          as="img"
                                          src={fileUrl}
                                          alt={f.originalName}
                                          w="48px" h="48px"
                                          objectFit="cover"
                                          borderRadius="8px"
                                          border={`1px solid ${colors.neutral[200]}`}
                                          flexShrink={0}
                                        />
                                      ) : (
                                        <Box w="48px" h="48px" borderRadius="8px" bg={colors.brand[50]} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                                          <Icon as={FiFileText} color={colors.brand[600]} boxSize={5} />
                                        </Box>
                                      )}
                                      <Box minW={0} flex={1}>
                                        <Text fontSize="12px" fontWeight="700" color={colors.neutral[800]} noOfLines={1} m={0}>{f.originalName || f.fileName || "Prescription"}</Text>
                                        <Text fontSize="10px" color={colors.neutral[400]} m={0}>{f.mimeType || ""}{f.size ? ` · ${(f.size / 1024).toFixed(0)} KB` : ""}</Text>
                                      </Box>
                                      <Button
                                        as="a" href={fileUrl} target="_blank" rel="noopener noreferrer"
                                        size="xs" variant="outline" borderColor={colors.brand[200]} color={colors.brand[600]}
                                        fontWeight="700" rightIcon={<FiExternalLink size={10} />}
                                        flexShrink={0}
                                      >
                                        Open
                                      </Button>
                                    </Flex>
                                  );
                                })}
                              </Stack>
                            )}
                            {/* Medicine summary */}
                            {Array.isArray(order.items) && order.items.length > 0 && (
                              <Text fontSize="11px" color={colors.neutral[400]} mt={2} m={0}>
                                {order.items.length} medicine{order.items.length !== 1 ? "s" : ""}: {order.items.slice(0, 3).map(i => i.name || i.product_name).filter(Boolean).join(", ")}{order.items.length > 3 ? ` +${order.items.length - 3} more` : ""}
                              </Text>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                );
              })()}
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Scan Prescription Modal */}

        <Modal isOpen={isScanOpen} onClose={onScanClose} size="xl" scrollBehavior="inside">
          <ModalOverlay bg="blackAlpha.600" />
          <ModalContent borderRadius="20px" mx={4}>
            <ModalHeader borderBottom={`1px solid ${colors.neutral[100]}`} pb={4}>
              <Flex align="center" gap={3}>
                <Box w="36px" h="36px" borderRadius="10px" bg={colors.brand[50]} display="flex" alignItems="center" justifyContent="center">
                  <Icon as={FiCamera} color={colors.brand[600]} boxSize={4} />
                </Box>
                <Box>
                  <Text m={0} fontWeight="800" fontSize="16px">Scan Prescription</Text>
                  <Text m={0} fontSize="12px" color={colors.neutral[500]} fontWeight="400">AI reads and detects medicines automatically</Text>
                </Box>
              </Flex>
            </ModalHeader>
            <ModalCloseButton top={4} />
            <ModalBody py={5}>
              {/* Upload another */}
              <Flex
                align="center" gap={3} px={4} py={3}
                border={`1.5px dashed ${colors.neutral[300]}`}
                borderRadius="14px" bg={colors.neutral[50]}
                cursor="pointer"
                _hover={{ borderColor: colors.brand[300], bg: colors.brand[50] }}
                transition="all .15s"
                onClick={() => scanFileInputRef.current?.click()}
                mb={4}
              >
                <Icon as={FiUploadCloud} color={colors.brand[500]} boxSize={5} />
                <Box flex={1} minW={0}>
                  {scanFile ? (
                    <Text fontSize="13px" fontWeight="700" color={colors.neutral[800]} m={0} noOfLines={1}>{scanFile.name}</Text>
                  ) : (
                    <Text fontSize="13px" fontWeight="700" color={colors.neutral[500]} m={0}>Upload prescription (JPG, PNG, PDF)</Text>
                  )}
                </Box>
                {scanFile && <Text fontSize="11px" color={colors.neutral[400]} m={0} flexShrink={0}>Change</Text>}
              </Flex>

              {/* Scanning state */}
              {scanLoading && (
                <Flex direction="column" align="center" gap={3} py={8}>
                  <Spinner size="lg" color={colors.brand[500]} thickness="3px" />
                  <Text fontSize="14px" fontWeight="700" color={colors.neutral[700]} m={0}>Scanning prescription…</Text>
                  <Text fontSize="12px" color={colors.neutral[500]} m={0}>AI is reading and detecting medicines</Text>
                </Flex>
              )}

              {/* Error */}
              {!scanLoading && scanError && (
                <Flex align="center" gap={2} bg="#fff1f2" border="1px solid #fecdd3" borderRadius="10px" p={3} mb={4}>
                  <Text fontSize="13px" color="#b91c1c" m={0}>{scanError}</Text>
                </Flex>
              )}

              {/* Results */}
              {!scanLoading && scanSuggestions.length > 0 && (
                <>
                  <Flex align="center" justify="space-between" mb={3}>
                    <Text fontWeight="800" fontSize="14px" color={colors.neutral[900]} m={0}>
                      Detected {scanSuggestions.length} medicine{scanSuggestions.length > 1 ? "s" : ""}
                    </Text>
                    <Badge colorScheme="teal" borderRadius="full" px={3} py={1}>
                      {scanSuggestions.filter(m => m?.product_id && cartItems.some(i => i.productId === m.product_id)).length} in cart
                    </Badge>
                  </Flex>
                  <Stack spacing={2.5}>
                    {scanSuggestions.map((med, idx) => {
                      const key = med?.product_id || med?.extracted_name || String(idx);
                      const busy = scanAdding[key];
                      const pid = med?.product_id;
                      const cartItem = pid ? cartItems.find(i => i.productId === pid) : null;
                      const inCart = !!cartItem;
                      const qty = cartItem?.quantity || 0;
                      const name = med?.product_name || med?.Name || med?.extracted_name || "Unknown";
                      const salt = med?.extracted_salt_name || med?.composition || med?.key_ingredients || "";
                      const manufacturer = med?.Manufacturer || med?.manufacturer_name || med?.manufacturer || med?.marketer || "";
                      const packSize = med?.pack_size_label || med?.pack_size || med?.packSize || "";
                      const strength = med?.extracted_strength || "";
                      const frequency = med?.extracted_frequency || "";
                      const duration = med?.extracted_duration || "";
                      const price = med?.mrp != null && Number(med?.mrp) > 0 ? Number(med.mrp).toFixed(0) : null;
                      const matched = Boolean(med?.matched);
                      return (
                        <Flex
                          key={key}
                          align="center"
                          gap={3}
                          px={4} py={3}
                          bg={inCart ? colors.brand[50] : "white"}
                          border={`1px solid ${inCart ? colors.brand[200] : colors.neutral[200]}`}
                          borderRadius="14px"
                          transition="all .15s"
                        >
                          <Box flex={1} minW={0}>
                            <Flex align="center" gap={2} mb="2px">
                              <Text fontSize="13px" fontWeight="800" color={colors.neutral[900]} m={0} noOfLines={1}>{name}</Text>
                              {!matched && <Badge colorScheme="orange" fontSize="9px" borderRadius="full">Unmatched</Badge>}
                              {price && <Badge colorScheme="teal" fontSize="9px" borderRadius="full">₹{price}</Badge>}
                            </Flex>
                            {salt && <Text fontSize="11px" color={colors.neutral[500]} m={0} noOfLines={1}>{salt}</Text>}
                            {(packSize || manufacturer) && (
                              <Text fontSize="11px" color={colors.neutral[400]} m={0} noOfLines={1}>
                                {[packSize, manufacturer].filter(Boolean).join(" · ")}
                              </Text>
                            )}
                            <Flex gap={1.5} wrap="wrap" mt={1}>
                              {strength && <Badge colorScheme="gray" fontSize="9px">{strength}</Badge>}
                              {frequency && <Badge colorScheme="green" fontSize="9px">{frequency}</Badge>}
                              {duration && <Badge colorScheme="gray" fontSize="9px">{duration}</Badge>}
                            </Flex>
                          </Box>
                          {inCart ? (
                            <HStack spacing={1} flexShrink={0}>
                              <IconButton
                                icon={qty <= 1 ? <FiTrash2 /> : <FiMinus />}
                                size="xs"
                                borderRadius="full"
                                colorScheme={qty <= 1 ? "red" : "gray"}
                                variant="outline"
                                aria-label="Decrease"
                                isLoading={busy}
                                onClick={() => handleHeroQtyChange(pid, qty - 1)}
                              />
                              <Text fontSize="14px" fontWeight="800" color={colors.neutral[900]} m={0} minW="20px" textAlign="center">{qty}</Text>
                              <IconButton
                                icon={<FiPlus />}
                                size="xs"
                                borderRadius="full"
                                bgGradient={gradients.brand}
                                color="white"
                                _hover={{ bgGradient: gradients.brandBold }}
                                aria-label="Increase"
                                isLoading={busy}
                                onClick={() => handleHeroQtyChange(pid, qty + 1)}
                              />
                            </HStack>
                          ) : (
                            <Button
                              size="sm"
                              bgGradient={gradients.brand}
                              color="white"
                              leftIcon={<FiPlus />}
                              borderRadius="full"
                              px={4}
                              flexShrink={0}
                              isLoading={busy}
                              loadingText="Adding"
                              _hover={{ bgGradient: gradients.brandBold }}
                              onClick={() => handleScanAdd(med, idx)}
                              isDisabled={!pid}
                              title={!pid ? "Medicine not matched in catalog — add manually" : ""}
                            >
                              Add
                            </Button>
                          )}
                        </Flex>
                      );
                    })}
                  </Stack>
                </>
              )}
            </ModalBody>
            <ModalFooter borderTop={`1px solid ${colors.neutral[100]}`} pt={3}>
              <Text fontSize="12px" color={colors.neutral[400]} m={0} mr="auto">
                {scanAddedIds.size > 0 ? `${scanAddedIds.size} medicine${scanAddedIds.size > 1 ? "s" : ""} added to cart` : "Select medicines to add to your cart"}
              </Text>
              <Button borderRadius="full" variant="ghost" fontWeight="700" colorScheme="red" size="sm"
                onClick={() => { onScanClose(); setScanSuggestions([]); setScanError(""); setScanFile(null); setScanAddedIds(new Set()); }}
              >
                Clear
              </Button>
              <Button borderRadius="full" fontWeight="700" onClick={onScanClose}>
                Done
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Removed legacy hero/offers/active-orders/reorders/categories sections for a focused layout */}
        {false && (
          <><Box><Box>
            <Box
              bgGradient={gradients.heroBg}
              border={`1px solid ${colors.brand[100]}`}
              borderRadius={{ base: "20px", xl: "26px" }}
              px={{ base: 6, md: 8, xl: 10 }}
              py={{ base: 6, md: 7 }}
              boxShadow={shadows.md}
              position="relative"
              overflow="hidden"
            >
              {/* Soft decorative ring — intentional, anchored to the hero */}
              <Box
                position="absolute"
                top="-80px"
                right="-60px"
                w="320px"
                h="320px"
                borderRadius="full"
                bg={`radial-gradient(circle at 30% 30%, ${colors.brand[100]}, transparent 65%)`}
                pointerEvents="none"
              />
              <Box
                position="absolute"
                bottom="-100px"
                left="-80px"
                w="260px"
                h="260px"
                borderRadius="full"
                bg={`radial-gradient(circle at 50% 50%, ${colors.accent[50]}, transparent 62%)`}
                pointerEvents="none"
              />
              <Flex
                direction={{ base: "column", xl: "row" }}
                align={{ base: "stretch", xl: "center" }}
                justify="space-between"
                gap={{ base: 6, xl: 8 }}
                position="relative"
              >
                <Box flex="1" minW={0}>
                  <HStack
                    spacing={2}
                    display="inline-flex"
                    bg="white"
                    color={colors.accent[700]}
                    border={`1px solid ${colors.accent[100]}`}
                    borderRadius="full"
                    px={3}
                    py="7px"
                    mb={4}
                    boxShadow={shadows.xs}
                  >
                    <Icon as={FiBell} />
                    <Text fontSize="12px" fontWeight="800" m={0}>
                      {refillHint}
                    </Text>
                  </HStack>
                  <Text
                    fontSize={{ base: "30px", md: "34px", xl: "36px" }}
                    fontWeight="900"
                    color={colors.neutral[900]}
                    m={0}
                    letterSpacing="-0.04em"
                    lineHeight="1.02"
                  >
                    {firstName ? `${greeting}, ${firstName}` : greeting}
                  </Text>
                  <Text
                    fontSize={{ base: "14px", xl: "15px" }}
                    color={colors.neutral[500]}
                    m={0}
                    mt={2.5}
                    lineHeight="1.65"
                    maxW="640px"
                  >
                    Upload your prescription — we'll find the best price from nearby pharmacies in
                    30 seconds. Or search for a specific medicine below.
                  </Text>
                  <HStack spacing={3} mt={4} wrap="wrap">
                    <Button
                      onClick={() => navigate("/medicine")}
                      bgGradient={gradients.brand}
                      color="white"
                      size="md"
                      fontWeight="800"
                      leftIcon={<FiSearch />}
                      _hover={{ bgGradient: gradients.brandBold, transform: "translateY(-1px)", boxShadow: shadows.brandHover }}
                    >
                      Browse medicines
                    </Button>
                    <Button
                      onClick={() => navigate("/upload-prescription")}
                      variant="outline"
                      borderColor={colors.brand[200]}
                      color={colors.brand[700]}
                      bg="white"
                      size="md"
                      fontWeight="800"
                      leftIcon={<FiZap />}
                      _hover={{ bg: colors.brand[50] }}
                    >
                      Upload prescription
                    </Button>
                  </HStack>
                  <HStack spacing={1} mt={3}>
                    <Text fontSize="12px" color={colors.neutral[500]} m={0}>Are you a pharmacist?</Text>
                    <Button
                      onClick={() => navigate("/login")}
                      variant="link"
                      size="sm"
                      color={colors.brand[700]}
                      fontWeight="800"
                      p={0}
                      h="auto"
                      minW={0}
                    >
                      Login as pharmacist
                    </Button>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3} maxW="860px" mt={5}>
                    {statChips.map((item) => (
                      <Box
                        key={item.label}
                        bg="white"
                        border={`1px solid ${colors.neutral[200]}`}
                        borderRadius={radii.md}
                        px={4}
                        py={3}
                        minH="82px"
                        transition="all 0.18s ease"
                        _hover={{ transform: "translateY(-2px)", boxShadow: shadows.md, borderColor: colors.brand[200] }}
                      >
                        <HStack spacing={3}>
                          <Box
                            w="36px"
                            h="36px"
                            borderRadius="10px"
                            bg={item.bg}
                            color={item.fg}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                          >
                            <Icon as={item.icon} boxSize={5} />
                          </Box>
                          <Box minW={0}>
                            <Text fontSize="10px" fontWeight="800" color={colors.neutral[400]} textTransform="uppercase" letterSpacing="0.1em" m={0}>
                              {item.eyebrow}
                            </Text>
                            <Text fontSize="13px" fontWeight="800" color={colors.neutral[900]} mt={0.5} m={0} lineHeight="1.35" noOfLines={2}>
                              {item.label}
                            </Text>
                          </Box>
                        </HStack>
                      </Box>
                    ))}
                  </SimpleGrid>

                  <HStack spacing={5} mt={4} wrap="wrap">
                    <SocialProof orderCount="10,000+" />
                    <HStack spacing={1.5} color={colors.neutral[500]}>
                      <Icon as={FiShield} color={colors.brand[600]} />
                      <Text fontSize="12px" fontWeight="700" m={0}>Licensed pharmacies only</Text>
                    </HStack>
                    <HStack spacing={1.5} color={colors.neutral[500]}>
                      <Icon as={FiClock} color={colors.brand[600]} />
                      <Text fontSize="12px" fontWeight="700" m={0}>Same-day delivery</Text>
                    </HStack>
                  </HStack>
                </Box>

                <Box
                  display={{ base: "none", xl: "block" }}
                  w={{ xl: "340px", "2xl": "400px" }}
                  flexShrink={0}
                >
                  <HeroFlowCarousel />
                </Box>
              </Flex>
            </Box>
          </Box>
        </Box>

        <Box px={{ base: "20px", md: "28px", xl: "36px" }} pb={6}>
          <Box maxW="1560px" mx="auto">
            <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={4} id="offers">
              {offerCards.map((card) => (
                <Box
                  key={card.title}
                  bg="white"
                  border={`1px solid ${colors.neutral[200]}`}
                  borderRadius="22px"
                  px={5}
                  py={5}
                  boxShadow={shadows.sm}
                  _hover={{ transform: "translateY(-2px)", boxShadow: shadows.md }}
                  transition="all 0.18s ease"
                >
                  <HStack justify="space-between" align="flex-start">
                    <Box>
                      <Text fontSize="11px" fontWeight="800" color={colors.brand[600]} textTransform="uppercase" letterSpacing="0.12em" m={0}>
                        {card.eyebrow}
                      </Text>
                      <Text fontSize="18px" fontWeight="800" color={colors.neutral[900]} mt={2} m={0} lineHeight="1.35">
                        {card.title}
                      </Text>
                      <Text fontSize="14px" color={colors.neutral[600]} mt={2.5} lineHeight="1.7" m={0}>
                        {card.copy}
                      </Text>
                    </Box>
                    <Box
                      w="42px"
                      h="42px"
                      borderRadius="14px"
                      bg={colors.brand[50]}
                      color={colors.brand[700]}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                    >
                      <Icon as={card.icon} boxSize={5} />
                    </Box>
                  </HStack>
                </Box>
              ))}
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4} mt={4}>
              <Box
                bg="white"
                border={`1px solid ${colors.neutral[200]}`}
                borderRadius="24px"
                p={5}
                boxShadow={shadows.sm}
              >
                <Flex justify="space-between" align="flex-start" gap={4}>
                  <Box>
                    <Text fontSize="12px" fontWeight="800" color={colors.brand[600]} textTransform="uppercase" letterSpacing="0.12em" m={0}>
                      Active orders
                    </Text>
                    <Text fontSize="24px" fontWeight="900" color={colors.neutral[900]} mt={2} m={0}>
                      {activeOrders.length ? "Track your current delivery" : "No active delivery right now"}
                    </Text>
                    <Text fontSize="14px" color={colors.neutral[600]} mt={2} lineHeight="1.7" m={0}>
                      {activeOrders.length
                        ? "You have a live medicine order in progress. Jump back in to review status and delivery details."
                        : "When you place a new order, live fulfillment status will appear here instead of being buried elsewhere."}
                    </Text>
                  </Box>
                  <Box
                    w="48px"
                    h="48px"
                    borderRadius="16px"
                    bg={activeOrders.length ? colors.success[50] : colors.neutral[100]}
                    color={activeOrders.length ? colors.success[700] : colors.neutral[500]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Icon as={FiPackage} boxSize={6} />
                  </Box>
                </Flex>
                <HStack spacing={3} mt={5} wrap="wrap">
                  <Button
                    bgGradient={gradients.brand}
                    color="white"
                    fontWeight="800"
                    onClick={() => navigate(activeOrders.length ? "/orderDetails" : "/upload-prescription")}
                  >
                    {activeOrders.length ? "View active order" : "Start a new order"}
                  </Button>
                  <Button
                    variant="outline"
                    borderColor={colors.brand[200]}
                    color={colors.brand[700]}
                    fontWeight="800"
                    onClick={() => navigate("/orderDetails")}
                  >
                    Order history
                  </Button>
                </HStack>
              </Box>

              <Box
                bg="white"
                border={`1px solid ${colors.neutral[200]}`}
                borderRadius="24px"
                p={5}
                boxShadow={shadows.sm}
              >
                <Flex justify="space-between" align="flex-start" gap={4}>
                  <Box>
                    <Text fontSize="12px" fontWeight="800" color={colors.brand[600]} textTransform="uppercase" letterSpacing="0.12em" m={0}>
                      Quick reorder
                    </Text>
                    <Text fontSize="24px" fontWeight="900" color={colors.neutral[900]} mt={2} m={0}>
                      Revisit recent medicine orders faster
                    </Text>
                  </Box>
                  <Box
                    w="48px"
                    h="48px"
                    borderRadius="16px"
                    bg={colors.warning[50]}
                    color={colors.warning[700]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Icon as={FiRefreshCcw} boxSize={6} />
                  </Box>
                </Flex>
                <Stack spacing={3} mt={5}>
                  {(quickReorders.length ? quickReorders : [{ id: "empty", title: "No recent prescription orders yet", subtitle: "Your last completed orders will appear here for one-tap reorder shortcuts.", eta: "Start ordering" }]).map((item) => (
                    <Flex
                      key={item.id}
                      justify="space-between"
                      align="center"
                      gap={3}
                      p={3}
                      borderRadius="16px"
                      bg={colors.neutral[50]}
                      border={`1px solid ${colors.neutral[200]}`}
                    >
                      <Box minW={0}>
                        <Text fontSize="14px" fontWeight="800" color={colors.neutral[900]} m={0} noOfLines={1}>
                          {item.title}
                        </Text>
                        <Text fontSize="12px" color={colors.neutral[500]} mt={1} m={0}>
                          {item.subtitle}
                        </Text>
                      </Box>
                      <Button
                        size="sm"
                        variant="outline"
                        borderColor={colors.brand[200]}
                        color={colors.brand[700]}
                        fontWeight="800"
                        onClick={() => navigate(item.id === "empty" ? "/medicine" : "/orderDetails")}
                      >
                        {item.id === "empty" ? "Browse" : item.eta}
                      </Button>
                    </Flex>
                  ))}
                </Stack>
              </Box>
            </SimpleGrid>

            <Box
              bg="white"
              border={`1px solid ${colors.neutral[200]}`}
              borderRadius="24px"
              p={5}
              boxShadow={shadows.sm}
              mt={4}
            >
              <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} gap={4} wrap="wrap">
                <Box>
                  <Text fontSize="12px" fontWeight="800" color={colors.brand[600]} textTransform="uppercase" letterSpacing="0.12em" m={0}>
                    Health categories
                  </Text>
                  <Text fontSize="24px" fontWeight="900" color={colors.neutral[900]} mt={2} m={0}>
                    Browse common care journeys directly
                  </Text>
                </Box>
                <Button
                  variant="ghost"
                  color={colors.brand[700]}
                  fontWeight="800"
                  onClick={() => navigate("/medicine")}
                >
                  View catalog
                </Button>
              </Flex>
              <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3} mt={5}>
                {categoryCards.map((item) => (
                  <Box
                    key={item.title}
                    bg={item.tone}
                    color={item.fg}
                    borderRadius="18px"
                    px={4}
                    py={4}
                    border="1px solid rgba(15,23,42,0.06)"
                    _hover={{ transform: "translateY(-1px)" }}
                    transition="all 0.18s ease"
                  >
                    <Icon as={item.icon} boxSize={5} />
                    <Text fontSize="14px" fontWeight="800" mt={3} m={0}>
                      {item.title}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </Box>
        </Box>
        </>)}

      </Box>

      {/* Modals */}
      <Modal isOpen={modalIsOpen} onClose={() => setModalIsOpen(false)} size="md" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="16px" p={2}>
          <ModalHeader borderBottom="none" pt={6}>
            <Text fontSize="18px" fontWeight="800" color={colors.neutral[900]}>
              {addressModalMode === "list" ? "Saved Addresses" : "Add New Address"}
            </Text>
          </ModalHeader>
          <ModalCloseButton mt={4} mr={4} />
          <ModalBody pb={6}>
            {addressModalMode === "list" ? (
              <Stack spacing={3}>
                {(fullUser?.DeliveryAddress || []).length === 0 ? (
                  <Text fontSize="13px" color={colors.neutral[500]} textAlign="center" py={4}>
                    No saved addresses yet.
                  </Text>
                ) : (
                  (fullUser?.DeliveryAddress || []).map((addr, idx) => {
                    const text = typeof addr === "string" ? addr : (addr?.address || "");
                    return (
                      <Flex
                        key={`saved-addr-${idx}`}
                        align="center"
                        justify="space-between"
                        p={3}
                        borderRadius="12px"
                        border={`1px solid ${colors.neutral[200]}`}
                        bg={colors.neutral[50]}
                      >
                        <Text fontSize="13px" fontWeight="600" color={colors.neutral[800]} m={0} noOfLines={2} flex={1} pr={2}>
                          {text}
                        </Text>
                        <IconButton
                          aria-label="Delete address"
                          icon={<Icon as={FiTrash2} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => removeAddress(text)}
                        />
                      </Flex>
                    );
                  })
                )}
              </Stack>
            ) : (
              <Stack spacing={3}>
                <FormControl>
                  <FormLabel fontSize="13px">Address Name</FormLabel>
                  <Input value={addressName} onChange={(e) => setAddressName(e.target.value)} placeholder="Home, Office..." />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="13px">Street</FormLabel>
                  <Input value={streetName} onChange={(e) => setStreetName(e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="13px">Building number</FormLabel>
                  <Input value={buildingNumber} onChange={(e) => setBuildingNumber(e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="13px">Floor</FormLabel>
                  <Input value={floor} onChange={(e) => setFloor(e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="13px">Apartment</FormLabel>
                  <Input value={apartment} onChange={(e) => setApartment(e.target.value)} />
                </FormControl>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            {addressModalMode === "list" ? (
              <Button onClick={() => setModalIsOpen(false)} variant="ghost" fontWeight="700">Close</Button>
            ) : (
              <>
                <Button mr={3} onClick={() => setAddressModalMode("list")} variant="ghost" fontWeight="700">Back</Button>
                <Button bgGradient={gradients.brand} color="white" _hover={{ bgGradient: gradients.brandBold }} onClick={addAddress} fontWeight="700">
                  Save Address
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        initialFocusRef={initialRef}
        finalFocusRef={finalRef}
        isOpen={isOpen}
        onClose={closeChangePasswordModal}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel>Old Password</FormLabel>
              <Input id="oldPassword" type="password" ref={initialRef} placeholder="Old Password" />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>New Password</FormLabel>
              <Input id="newPassword" type="password" placeholder="New Password" />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Confirm Password</FormLabel>
              <Input id="confirmNewPassword" type="password" placeholder="Confirm New Password" />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleChangePass} bgGradient={gradients.brand} color="white" mr={3}>
              Save
            </Button>
            <Button onClick={closeChangePasswordModal}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Login Modal */}
      <Modal isOpen={isLoginModalOpen} onClose={onLoginModalClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="16px" p={2}>
          <ModalHeader textAlign="center" borderBottom="none" pt={6}>
            <Text fontSize="20px" fontWeight="800" color={colors.neutral[900]}>Sign in to continue</Text>
            <Text fontSize="13px" color={colors.neutral[500]} fontWeight="normal" mt={1}>
              {pendingAction === 'checkPrices'
                ? 'Check pharmacy prices and availability'
                : pendingAction === 'saveAddress'
                ? 'Save your delivery address'
                : 'Complete your order'}
            </Text>
          </ModalHeader>
          <ModalCloseButton mt={4} mr={4} />
          <ModalBody pb={6}>
            <LoginForm
              showLinks={false}
              onSuccess={() => {
                const action = pendingAction;
                onLoginModalClose();
                setPendingAction(null);
                if (action === 'checkPrices') {
                  setTimeout(() => handleCheckPrices(true), 800);
                } else if (action === 'saveAddress') {
                  setTimeout(() => addAddress(true), 800);
                }
              }}
            />
            <Text fontSize="12px" color={colors.neutral[400]} textAlign="center" mt={4}>
              Signing in allows you to save addresses, track orders, and get the best prices from nearby pharmacies.
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>

      <ToastContainer />
    </>
  );
};

export default Home;
